import { formatDisplayAmount } from "./base-app/helpers/amountDisplay";
import { useWalletFormReset } from "./base-app/hooks/useWalletFormReset";
import { getSwapInputPrivateBalance } from "./base-app/helpers/swapBalance";
import { WalletAmountInput } from "./base-app/components/WalletAmountInput";
import { PrivateBridgePanel } from "./base-app/components/PrivateBridgePanel";
import { anyPrivateBridgeDeployment } from "./joinsplit-v2/privateBridgeConfig";
import { withDeadline } from "./utils/withDeadline";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ethers } from "ethers";
import { amountValidation } from "./base-app/helpers/amountValidation";
import { paymentLinkNetworkError } from "./base-app/helpers/paymentLink";
import {
  useRelayFeeQuote,
  type RelayFeeQuoteInput,
} from "./base-app/hooks/useRelayFeeQuote";
import { useWalletTokenBalance } from "./base-app/hooks/useWalletTokenBalance";
import { useSendRecipient } from "./base-app/hooks/useSendRecipient";
import { flushSync } from "react-dom";
import { useDepositMergeWarmup } from "./joinsplit-v2/useDepositMergeWarmup";
import { BaseWalletShell } from "./base-app/components/BaseWalletShell";
import { BaseHandlePanel } from "./base-app/components/BaseHandlePanel";
import { BaseTransactionReview } from "./base-app/components/BaseTransactionReview";
import { BaseDepositEstimate } from "./base-app/components/BaseDepositEstimate";
import { fetchPrivateFeeRecovery } from "./joinsplit-v2/privateFeeRecovery";
import { usesPrivateFees } from "./joinsplit-v2/privateFees";
import { PrivateFeeSummary } from "./base-app/components/PrivateFeeSummary";
import { approvePrivateFeePolicy } from "./joinsplit-v2/privateFeeApproval";
import { useDepositMax } from "./base-app/hooks/useDepositMax";
import { styles } from "./styles";
import BasePaymentPage from "./components/BasePaymentPage";
import { useRangeScreening } from "./hooks/useRangeScreening";
import { useChain } from "./contexts/ChainContext";
import {
  useBaseWallet,
  useBaseShieldOperation,
  useBaseSendOperation,
  useBaseSwapQuote,
  useBaseSwapOperation,
  useBaseBridgeQuote,
  useBaseBridgeOperation,
  useBaseHistorySync,
  useBaseInboxOrNoteSync,
  useBaseProxyAuth,
  useEIP6963,
  useStealthHandleRegistration,
  useWalletNftDiscovery,
  useBaseAppActions,
  buildCustomNftToken,
} from "./base-app/hooks";
import type { OdosQuoteResponse } from "./base-app/hooks/useBaseSwapQuote";
import { BaseSwapTab } from "./base-app/components/BaseSwapTab";
import { BaseCrossChainTab } from "./base-app/components/BaseCrossChainTab";
import { BasePerpsTab } from "./base-app/components/BasePerpsTab";
import { BaseReceiveTab } from "./base-app/components/BaseReceiveTab";
import { BaseSendTab } from "./base-app/components/BaseSendTab";
import { BaseOperationProgress } from "./base-app/components/BaseOperationProgress";
import { BasePrivateBalancesCard } from "./base-app/components/BasePrivateBalancesCard";
import { balanceUsdValue } from "./utils/market-prices";
import { useMarketPrices } from "./app-shell/useMarketPrices";
import { evmMarketPriceIds } from "./utils/market-prices";
import { useBlockNetworkSwitch } from "./networks/WalletNetworkContext";
import { BaseHistoryCard } from "./base-app/components/BaseHistoryCard";
import { BaseRecoveryPanel } from "./base-app/components/BaseRecoveryPanel";
import { OnchainRecoveryPanel } from "./base-app/components/OnchainRecoveryPanel";
import { configuredEvmRecoveryRegistry } from "./joinsplit-v2/onchainRecoveryEvm";
import { RecoveryPhraseBackup } from "./base-app/components/RecoveryPhraseBackup";
import { getBaseReadProvider } from "./utils/base-read-provider";
import { useBaseStatusDismissal } from "./base-app/hooks/useBaseStatusDismissal";
import { useBaseConsolidation } from "./base-app/hooks/useBaseConsolidation";
import { startRecoveryPolling } from "./base-app/helpers/recoveryPolling";
import { hasActiveFlatJoinSplitV2Operation } from "./joinsplit-v2/walletLock";
import {
  replayRecoveryNote,
  type RecoveryContext,
  type RecoveryEnvelope,
} from "./joinsplit-v2/recoveryManagement";
import { assertFlatJoinSplitV2Note, flatTreeKey } from "./joinsplit-v2/notes";
import ComplianceToast from "./base-app/components/ComplianceToast";
import WalletModal from "./base-app/components/WalletModal";
import { useDogeOsConnectKit } from "./dogeos-app/connect/useDogeOsConnectKit";
import NftDiscoveryFields from "./base-app/components/NftDiscoveryFields";
import {
  buildInboxNoteRecordId,
  fetchPrivateCommitments,
  storePrivateCommitment,
  storePrivateHistoryEntry,
  storePrivateInboxNote,
} from "./api/private-data-storage";
import type {
  BaseHistoryEntry,
  BaseLogEntry,
  BaseStatusMessage,
  BaseStoredNote,
  BaseTabType,
} from "./base-app/types";
import { readEncryptedPendingInboxNotes } from "./base-app/helpers/pendingInboxNotes";
import {
  saveEncryptedBaseNotes,
  loadEncryptedJsonCache,
  saveEncryptedJsonCache,
} from "./base-app/helpers/storage";
import { hasDisplayBalance } from "./base-app/helpers/cachedBalance";
import {
  parseBatchRecipientFile,
  type BatchRecipientRow,
} from "./base-app/utils/batchFileParser";
import {
  BRIDGE_DESTINATION_CHAIN,
  BRIDGE_DESTINATION_CHAIN_ID,
  DEFAULT_BRIDGE_TOKEN,
  DEFAULT_PERPS_ASSET_TOKEN_ADDRESS,
  DEFAULT_PERPS_ASSET_TOKEN_DECIMALS,
  DEFAULT_PERPS_ASSET_TOKEN_NAME,
  DEFAULT_PERPS_ASSET_TOKEN_SYMBOL,
  PAYMENT_LINK_TOKEN_OPTIONS,
  SOLANA_BRIDGE_MINT_BY_BASE_TOKEN,
  ScreeningStatus,
  applyWrappedNftMode,
  buildEngineUrl,
  buildRuntimeToken,
  getNftDisplayLabel,
  isLikelySolanaPubkey,
  maskHex,
  parseMpk,
  parseTokenAmountBase,
  tokenOptionValue,
} from "./base-app/helpers/baseAppUtils";
import {
  formatInboxPublicKeyHex,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
  decryptInboxNote,
  parsePrivateAddress,
  encryptInboxNoteWithPublicKey,
} from "./utils/inbox-crypto";
import {
  prepareFlatBalanceSnapshot,
  synchronizeFlatJoinSplitV2Notes,
} from "./joinsplit-v2/sync";
import {
  createWalletSyncGuard,
  mergeValidatedFlatInboxNotes,
} from "./joinsplit-v2/walletSyncGuard";
import {
  validateFlatInboxCandidate,
  protectValidatedFlatInbox,
  fetchProtectedFlatInbox,
} from "./joinsplit-v2/inboxProtection";
import { formatMpkHex, hashMpkHex } from "./utils/format";
import {
  isStealthHandle,
  resolveDestinationStealthHandle,
} from "./utils/handle-resolver";
import { deriveRecoveryKeyFromRecipientKey } from "./utils/recovery";
import { computeNullifier } from "./joinsplit/keys";
import { NoteTokenType } from "./joinsplit/types";
import {
  EVM_CONFIG,
  PREVIOUS_POOL_MODE,
  BASE_SEPOLIA_TOKENS,
  getActiveBaseShieldedPoolAddress,
  type TokenInfo,
} from "./config";
import {
  deriveTokenHash,
  normalizeTokenSubID,
  type EvmTokenType,
} from "./operations/settlement-helpers";

function baseTokenHashType(token: TokenInfo): EvmTokenType {
  const tokenType = token.tokenType ?? "erc20";
  if (tokenType === "erc721" || tokenType === "erc1155") {
    return tokenType;
  }
  if (tokenType === "vnft") {
    return token.wrappedUnderlyingTokenType ?? "erc721";
  }
  return "erc20";
}

import { useTokenCatalog } from "./base-app/hooks/useTokenCatalog";
import {
  privateTokenPool,
  privatePoolForToken,
  privatePools,
  privateSpendSource,
} from "./joinsplit-v2/privatePools";
import { requestPrivateFees } from "./utils/relayer-client";
import {
  evaluateSyncHead,
  syncHeadFingerprint,
  syncHeadReadingFromResponses,
  type SyncHeadRecord,
  type SyncHeadReading,
} from "./base-app/helpers/syncHead";
import {
  applyIndexedSync,
  canApplyDelta,
  fetchChainDelta,
  isSyncIndexState,
  notesUnknownAtUnchangedHead,
  syncIndexAfterFullValidation,
  syncIndexVouchesForHead,
  type SyncIndexState,
  type SyncHeadCursors,
  type SyncChainDelta,
} from "./joinsplit-v2/syncIndex";
import {
  createTokenHashReader,
  fetchChainPage,
  fetchInboxLane,
} from "./base-app/helpers/syncDeltaFetch";

const EVM_PRICE_IDS = evmMarketPriceIds(Number(EVM_CONFIG.CHAIN_ID));

export default function BaseApp() {
  const {
    tokens: walletTokens,
    depositTokens,
    resolveSupportedToken,
    discover: discoverTokens,
  } = useTokenCatalog();
  const BASE_DEPOSIT_TOKEN_OPTIONS = depositTokens.filter(
    (token) =>
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED ||
      ((token.tokenType ?? "erc20") === "erc20" &&
        (!usesPrivateFees() || token.isNative || !!privateTokenPool())),
  );
  const BASE_PAYMENT_TOKEN_OPTIONS = EVM_CONFIG.JOINSPLIT_V2.ENABLED
    ? BASE_DEPOSIT_TOKEN_OPTIONS
    : PAYMENT_LINK_TOKEN_OPTIONS;
  const { chainId, getExplorerTxUrl } = useChain();
  const [activeTab, setActiveTab] = useState<BaseTabType>("shield");
  const [status, setStatus] = useState<BaseStatusMessage | null>(null);
  const [logs, setLogs] = useState<BaseLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [reviewOperation, setReviewOperation] = useState<
    "shield" | "send" | null
  >(null);
  const [operationStarting, setOperationStarting] = useState(false);
  const operationStartingRef = useRef(false);
  const operationStartIdRef = useRef(0);

  const [depositAmount, setDepositAmount] = useState("0.01");
  const [depositRecipient, setDepositRecipient] = useState("");
  const [sendRecipient, setSendRecipient] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sendMemo, setSendMemo] = useState("");
  const [sendBatchMode, setSendBatchMode] = useState(false);
  const [sendBatchRows, setSendBatchRows] = useState<BatchRecipientRow[]>([
    { recipient: "", amount: "" },
  ]);
  const batchFileInputRef = useRef<HTMLInputElement | null>(null);
  const [paymentLinkAmountInput, setPaymentLinkAmountInput] = useState("");
  const [paymentLinkToken, setPaymentLinkToken] = useState<TokenInfo | null>(
    null,
  );
  const [walletBalanceEth, setWalletBalanceEth] = useState<number | null>(null);
  const [balancesRefreshing, setBalancesRefreshing] = useState(false);
  const [privateBalanceSyncing, setPrivateBalanceSyncing] = useState(false);
  useBaseStatusDismissal(
    status,
    setStatus,
    loading || operationStarting || balancesRefreshing || privateBalanceSyncing,
  );
  const balanceSyncBusyRef = useRef(false);
  balanceSyncBusyRef.current = balancesRefreshing || privateBalanceSyncing;
  const [
    pendingBalanceValidationCommitments,
    setPendingBalanceValidationCommitments,
  ] = useState<Set<string>>(new Set());
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [selectedDepositToken, setSelectedDepositToken] = useState<TokenInfo>(
    BASE_SEPOLIA_TOKENS[0],
  );
  const [selectedSendToken, setSelectedSendToken] = useState<TokenInfo>(
    BASE_SEPOLIA_TOKENS[0],
  );
  const [useCustomDepositNft, setUseCustomDepositNft] = useState(false);
  const [customDepositNftAddress, setCustomDepositNftAddress] = useState("");
  const [customDepositNftType, setCustomDepositNftType] = useState<
    "erc721" | "erc1155"
  >("erc721");
  const [depositTokenSubID, setDepositTokenSubID] = useState(
    selectedDepositToken.tokenSubID ?? "",
  );
  const useWrappedNftMode = true;
  const [useCustomSendNft, setUseCustomSendNft] = useState(false);
  const [customSendNftAddress, setCustomSendNftAddress] = useState("");
  const [customSendNftType, setCustomSendNftType] = useState<
    "erc721" | "erc1155"
  >("erc721");
  const [sendTokenSubID, setSendTokenSubID] = useState(
    selectedSendToken.tokenSubID ?? "",
  );
  const [approving, setApproving] = useState(false);
  const [tokenHashMap, setTokenHashMap] = useState<Map<string, bigint>>(
    new Map(),
  );
  const [selectedSendTokenHash, setSelectedSendTokenHash] = useState<
    bigint | undefined
  >(undefined);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [walletOperationOpen, setWalletOperationOpen] = useState(false);
  useWalletFormReset(walletOperationOpen, activeTab, () => {
    setDepositAmount("");
    setSendAmount("");
    setSendBatchRows((rows) => rows.map((row) => ({ ...row, amount: "" })));
    setReviewOperation(null);
  });
  const {
    prices: tokenPrices,
    changes: tokenPriceChanges,
    refresh: fetchTokenPrices,
  } = useMarketPrices(EVM_PRICE_IDS);
  const [pendingSpentCommitments, setPendingSpentCommitments] = useState<
    Set<string>
  >(new Set());
  const [lastPaymentTxHash, setLastPaymentTxHash] = useState<string | null>(
    null,
  );
  const [screeningStatus, setScreeningStatus] =
    useState<ScreeningStatus>("idle");
  const [screeningMessage, setScreeningMessage] = useState<string | null>(null);
  const [screeningError, setScreeningError] = useState<string | null>(null);
  const lastScreenedAddressRef = useRef<string | null>(null);
  const screeningInFlightRef = useRef(false);

  const customDepositToken = useMemo(
    () =>
      buildCustomNftToken(
        customDepositNftAddress,
        customDepositNftType,
        depositTokenSubID,
        true,
      ),
    [customDepositNftAddress, customDepositNftType, depositTokenSubID],
  );
  const customSendToken = useMemo(
    () =>
      buildCustomNftToken(
        customSendNftAddress,
        customSendNftType,
        sendTokenSubID,
      ),
    [customSendNftAddress, customSendNftType, sendTokenSubID],
  );
  const bridgeableTokens = useMemo(
    () => BASE_SEPOLIA_TOKENS.filter((token) => !token.isNative),
    [],
  );
  const activeDepositToken = useMemo(() => {
    const token = useCustomDepositNft
      ? customDepositToken
      : buildRuntimeToken(selectedDepositToken, depositTokenSubID);
    return token ? applyWrappedNftMode(token, true) : token;
  }, [
    useCustomDepositNft,
    customDepositToken,
    selectedDepositToken,
    depositTokenSubID,
  ]);
  const activeSendToken = useMemo(
    () =>
      useCustomSendNft
        ? customSendToken
        : buildRuntimeToken(selectedSendToken, sendTokenSubID),
    [useCustomSendNft, customSendToken, selectedSendToken, sendTokenSubID],
  );
  const configuredPerpsAssetToken = useMemo<TokenInfo | null>(() => {
    const configured = BASE_SEPOLIA_TOKENS.find(
      (token) =>
        token.address.toLowerCase() ===
        DEFAULT_PERPS_ASSET_TOKEN_ADDRESS.toLowerCase(),
    );
    if (configured) return configured;
    if (!ethers.isAddress(DEFAULT_PERPS_ASSET_TOKEN_ADDRESS)) return null;
    return {
      address: DEFAULT_PERPS_ASSET_TOKEN_ADDRESS,
      symbol: DEFAULT_PERPS_ASSET_TOKEN_SYMBOL,
      name: DEFAULT_PERPS_ASSET_TOKEN_NAME,
      decimals: DEFAULT_PERPS_ASSET_TOKEN_DECIMALS,
      isNative: false,
    };
  }, []);

  // Swap state
  const [swapInputToken, setSwapInputToken] = useState<TokenInfo>(
    BASE_SEPOLIA_TOKENS[0],
  );
  const [swapOutputToken, setSwapOutputToken] = useState<TokenInfo>(
    BASE_SEPOLIA_TOKENS[1] || BASE_SEPOLIA_TOKENS[0],
  );
  const [swapAmount, setSwapAmount] = useState("");
  const [swapSlippageBps, setSwapSlippageBps] = useState("50");
  const [swapQuote, setSwapQuote] = useState<OdosQuoteResponse | null>(null);
  const [swapQuoteLoading, setSwapQuoteLoading] = useState(false);
  const [swapQuoteError, setSwapQuoteError] = useState<string | null>(null);
  const [swapQuoteRefreshKey, setSwapQuoteRefreshKey] = useState(0);
  const [crossChainInputToken, setCrossChainInputToken] =
    useState<TokenInfo>(DEFAULT_BRIDGE_TOKEN);
  const crossChainDestinationTokenAddress = useMemo(
    () =>
      SOLANA_BRIDGE_MINT_BY_BASE_TOKEN[
        crossChainInputToken.address.toLowerCase()
      ] || "",
    [crossChainInputToken.address],
  );
  const [crossChainAmount, setCrossChainAmount] = useState("");
  const crossChainSourceAmountBase = useMemo(
    () => parseTokenAmountBase(crossChainAmount, crossChainInputToken.decimals),
    [crossChainAmount, crossChainInputToken.decimals],
  );
  const crossChainSourceAmount = useMemo(() => {
    if (
      crossChainSourceAmountBase === null ||
      crossChainSourceAmountBase <= 0n
    ) {
      return "";
    }
    return ethers.formatUnits(
      crossChainSourceAmountBase,
      crossChainInputToken.decimals,
    );
  }, [crossChainInputToken.decimals, crossChainSourceAmountBase]);
  const [crossChainDestinationChain, setCrossChainDestinationChain] = useState(
    BRIDGE_DESTINATION_CHAIN,
  );
  const [crossChainDestinationAddress, setCrossChainDestinationAddress] =
    useState("");
  const crossChainRecipientMode = "public" as const;
  const [crossChainQuote, setCrossChainQuote] = useState<any | null>(null);
  const [crossChainQuoteLoading, setCrossChainQuoteLoading] = useState(false);
  const [crossChainQuoteError, setCrossChainQuoteError] = useState<
    string | null
  >(null);
  const [crossChainOrderStatus, setCrossChainOrderStatus] = useState<
    any | null
  >(null);
  const [
    crossChainDestinationResolutionLoading,
    setCrossChainDestinationResolutionLoading,
  ] = useState(false);
  const [
    crossChainDestinationResolutionError,
    setCrossChainDestinationResolutionError,
  ] = useState<string | null>(null);
  const [
    crossChainPublicRecipientAddress,
    setCrossChainPublicRecipientAddress,
  ] = useState<string | null>(null);

  const eip6963Providers = useEIP6963();
  // DogeOS Chikyu inside MyDoge V3: the connect kit's connectors join the
  // announced wallets (phase 2); on Base this is the announcement list.
  const walletChoices = useDogeOsConnectKit(eip6963Providers);

  const isPaymentMode =
    typeof window !== "undefined" &&
    window.location.pathname === `${EVM_CONFIG.NETWORK?.route ?? "/base"}/send`;
  const paymentLinkError = isPaymentMode
    ? paymentLinkNetworkError(window.location.search, EVM_CONFIG.CHAIN_ID)
    : null;
  const enableRangeScreening = EVM_CONFIG.ENABLE_RANGE_SCREENING;
  const [paymentAmountLocked, setPaymentAmountLocked] = useState(false);
  const [paymentRecipientLocked, setPaymentRecipientLocked] = useState(false);
  const [paymentMintLocked, setPaymentMintLocked] = useState(false);
  const [paymentTokenPending, setPaymentTokenPending] = useState(false);
  const [paymentTokenError, setPaymentTokenError] = useState(false);
  const proxyAuthInFlightRef = useRef<Promise<string> | null>(null);
  const proxyAuthWarmWalletRef = useRef<string>("");
  const derivationSignatureB64Ref = useRef<string>("");
  const walletSyncGuardRef = useRef(createWalletSyncGuard());
  // Pool token hashes are immutable per (pool, tree, token); cached after the first read.
  const tokenHashReaderRef = useRef(
    createTokenHashReader({
      chainId: EVM_CONFIG.CHAIN_ID,
      provider: () => getBaseReadProvider(),
    }),
  );
  const notesRef = useRef<typeof notes>([]);
  const prevWalletConnectedRef = useRef<boolean>(false);
  const NOTES_API_URL = EVM_CONFIG.PROVER_URL;

  const addLog = useCallback(
    (
      message: string,
      type: "info" | "success" | "error" | "warning" = "info",
      txHash?: string,
    ) => {
      setLogs((prev) =>
        [
          { id: Date.now(), message, type, timestamp: new Date(), txHash },
          ...prev,
        ].slice(0, 60),
      );
    },
    [],
  );

  const waitForNextPaint = useCallback(async () => {
    if (typeof window === "undefined") return;
    await new Promise<void>((resolve) => {
      window.requestAnimationFrame(() => resolve());
    });
  }, []);

  const {
    wallet,
    notes,
    setNotes,
    connectWallet,
    disconnectWallet,
    connectedWalletName,
    connectError,
  } = useBaseWallet({ addLog, derivationSignatureB64Ref });
  // A failed connection was only logged; say why on screen.
  useEffect(() => {
    if (connectError) setStatus({ type: "error", message: connectError });
  }, [connectError]);
  const walletSyncScope = useMemo(
    () => ({}),
    [
      wallet.connected,
      wallet.address,
      wallet.spendingKeys,
      wallet.spendingKeys?.mpk,
      wallet.signer,
    ],
  );
  // Invalidate before async continuations can observe another wallet's notes.
  walletSyncGuardRef.current.setScope(walletSyncScope);
  useEffect(() => {
    const guard = walletSyncGuardRef.current;
    guard.setScope(walletSyncScope);
    return () => guard.clearScope(walletSyncScope);
  }, [walletSyncScope]);
  useRangeScreening({
    enabled: enableRangeScreening,
    connected: wallet.connected,
    address: wallet.address,
    network: "base",
    addLog,
    setStatus,
    setScreeningStatus,
    setScreeningMessage,
    setScreeningError,
    lastScreenedAddressRef,
    screeningInFlightRef,
  });
  const { privateBalance } = useBaseInboxOrNoteSync({
    address: wallet.address,
    notes,
    setNotes,
  });
  useEffect(() => {
    void discoverTokens(notes);
  }, [discoverTokens, notes]);
  const {
    authHeadersForPath,
    clearProxyAuthToken,
    ensureProxyAuthToken,
    proxyAuthReady,
    proxyAuthPending,
    proxyAuthError,
  } = useBaseProxyAuth({
    address: wallet.address,
    signer: wallet.signer,
    spendingKeys: wallet.spendingKeys,
    proxyAuthInFlightRef,
    derivationSignatureB64Ref,
  });
  const baseActionsEnabled =
    wallet.connected &&
    proxyAuthReady &&
    !paymentTokenPending &&
    !paymentTokenError;
  const onchainRecoveryEnabled = !!configuredEvmRecoveryRegistry(
    EVM_CONFIG.CHAIN_ID,
  );
  const recoveryReady =
    wallet.connected &&
    !!wallet.spendingKeys &&
    (proxyAuthReady || onchainRecoveryEnabled);
  useBaseConsolidation({
    wallet,
    enabled: baseActionsEnabled,
    notes,
    setNotes,
    addLog,
    isPaused: () =>
      balanceSyncBusyRef.current ||
      loading ||
      approving ||
      operationStarting ||
      !!reviewOperation ||
      walletOperationOpen,
  });
  const recoveryContext = useMemo<RecoveryContext | null>(() => {
    if (
      !baseActionsEnabled ||
      !wallet.spendingKeys ||
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED
    )
      return null;
    const keys = wallet.spendingKeys;
    // Capture per operation in the panel; do not pin a pre-effect generation
    // here because React StrictMode deliberately tears down/restarts effects.
    const isCurrent = () =>
      walletSyncGuardRef.current.capture(walletSyncScope)();
    return {
      scope: {
        chainId: EVM_CONFIG.CHAIN_ID,
        poolAddress: EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
        accountAddress: wallet.address,
        spendingKey: keys.spendingKey,
      },
      keys,
      baseUrl: NOTES_API_URL,
      isCurrent,
      headers: async (path) =>
        authHeadersForPath(`/api/v1${path}`, {
          expectedRecipientKey: await hashMpkHex(formatMpkHex(keys.mpk)),
          interactive: true,
        }),
    };
  }, [
    baseActionsEnabled,
    wallet.spendingKeys,
    wallet.address,
    walletSyncScope,
    authHeadersForPath,
    NOTES_API_URL,
  ]);
  useEffect(() => {
    setLogs([]);
    setPrivateBalanceSyncing(false);
    setBalancesRefreshing(false);
    setPendingBalanceValidationCommitments(new Set());
  }, [walletSyncScope]);
  const restoreManagedRecoveryNotes = useCallback(
    async (
      candidates: {
        enc: RecoveryEnvelope;
        note: import("./joinsplit/types").JoinSplitNote;
      }[],
      operationCurrent: () => boolean,
      signal?: AbortSignal,
    ): Promise<number> => {
      if (!recoveryContext || !wallet.signer?.provider || !wallet.spendingKeys)
        throw new Error(
          "Connect the original wallet to verify recovered notes",
        );
      const isCurrent = () => operationCurrent() && recoveryContext.isCurrent();
      const requireCurrent = () => {
        if (!isCurrent())
          throw new Error(
            "Wallet changed during recovery; original file retained",
          );
      };
      requireCurrent();
      const initialCommitments = new Set(
        notes.map((stored) => stored.note.commitment.toLowerCase()),
      );
      const authoritative = await synchronizeFlatJoinSplitV2Notes({
        provider: getBaseReadProvider(),
        spendingKeys: wallet.spendingKeys,
        notes: candidates.map(({ note }) => ({ note, timestamp: Date.now() })),
      });
      requireCurrent();
      // Recovery is additive: do not remove existing notes or resurrect notes spent during this check.
      setNotes((existing) => {
        if (!isCurrent()) return existing;
        const next = new Map(
          existing.map((stored) => [
            stored.note.commitment.toLowerCase(),
            stored,
          ]),
        );
        for (const [key, stored] of authoritative.validated) {
          if (!next.has(key) && !initialCommitments.has(key))
            next.set(key, stored);
        }
        return [...next.values()];
      });
      const deliveries = new Set<string>();
      for (const candidate of candidates) {
        const validated = authoritative.validated.get(
          candidate.note.commitment.toLowerCase(),
        );
        if (!validated || validated.spent) continue;
        try {
          await assertFlatJoinSplitV2Note({
            note: candidate.note,
            chainId: recoveryContext.scope.chainId,
            poolAddress: recoveryContext.scope.poolAddress,
            expectedMpk: recoveryContext.keys.mpk,
            tokenAddress: validated.note.tokenData!.tokenAddress,
            expectedTokenHash: validated.note.tokenHash,
          });
        } catch {
          continue;
        }
        requireCurrent();
        const delivery = await replayRecoveryNote(
          { ...recoveryContext, isCurrent, signal },
          candidate.enc,
        );
        requireCurrent();
        if (deliveries.has(delivery.deliveryId)) continue;
        const headers = await recoveryContext.headers("/notes/promote");
        requireCurrent();
        await protectValidatedFlatInbox({
          baseUrl: recoveryContext.baseUrl,
          recipientKey: await hashMpkHex(
            formatMpkHex(recoveryContext.keys.mpk),
          ),
          headers,
          candidates: [
            {
              note: candidate.note,
              enc: {
                ...candidate.enc,
                deliveryId: delivery.deliveryId,
                storageClass: "pending",
              },
            },
          ],
          validated: authoritative.validated,
          isCurrent,
          signal,
          scope: {
            chainId: Number(recoveryContext.scope.chainId),
            poolAddress: recoveryContext.scope.poolAddress,
            mpk: recoveryContext.keys.mpk,
          },
        });
        requireCurrent();
        deliveries.add(delivery.deliveryId);
      }
      return authoritative.validated.size;
    },
    [recoveryContext, wallet.signer, wallet.spendingKeys, notes, setNotes],
  );
  const handleProxyAuthClick = useCallback(async () => {
    setStatus(null);
    try {
      await ensureProxyAuthToken({ interactive: true });
      setStatus({ type: "success", message: "Private actions enabled." });
    } catch {
      // The auth hook supplies the inline notice and retry button.
    }
  }, [ensureProxyAuthToken]);
  const retryManagedRecoverySettlement = useCallback(
    async (
      record: import("./joinsplit-v2/settlementRetry").SavedFlatSettlementRetry,
      operationCurrent: () => boolean,
      signal?: AbortSignal,
    ) => {
      if (!recoveryContext || !wallet.signer)
        throw new Error(
          "Connect the original wallet to retry its saved transaction",
        );
      const { retrySavedFlatSettlement } =
        await import("./joinsplit-v2/settlementRetry");
      await retrySavedFlatSettlement({
        scope: recoveryContext.scope,
        recordId: record.id,
        calldataHash: record.calldataHash,
        signer: wallet.signer,
        provider: getBaseReadProvider(),
        isCurrent: () => operationCurrent() && recoveryContext.isCurrent(),
        signal,
        onLog: addLog,
        backupOptions: {
          baseUrl: recoveryContext.baseUrl,
          headers: await recoveryContext.headers("/recovery/journal"),
        },
      });
    },
    [recoveryContext, wallet.signer, addLog],
  );

  useEffect(() => {
    const address = wallet.address.trim().toLowerCase();
    if (!address) {
      proxyAuthWarmWalletRef.current = "";
      return;
    }
    if (!wallet.connected || !wallet.spendingKeys || !wallet.signer) {
      return;
    }
    if (proxyAuthWarmWalletRef.current === address) {
      return;
    }

    let cancelled = false;
    proxyAuthWarmWalletRef.current = address;
    void ensureProxyAuthToken({ interactive: true }).catch((err) => {
      if (cancelled) return;
      proxyAuthWarmWalletRef.current = "";
      console.warn("[Base] Proxy auth prewarm skipped:", err);
    });

    return () => {
      cancelled = true;
    };
  }, [
    wallet.address,
    wallet.connected,
    wallet.signer,
    wallet.spendingKeys,
    ensureProxyAuthToken,
  ]);

  const {
    historyEntries,
    historyHasMore,
    historyPageLoading,
    historyPageError,
    loadMoreHistory,
    mergeHistoryEntries,
    pushHistoryEntry,
    syncHistoryEntries,
    storeHistoryEvent,
  } = useBaseHistorySync({
    address: wallet.address,
    spendingKeys: wallet.spendingKeys,
    notesApiUrl: NOTES_API_URL,
    cacheSecret: derivationSignatureB64Ref.current,
    authHeadersForPath,
    addLog,
  });
  const { handleShield, depositProgress, setDepositProgress } =
    useBaseShieldOperation({
      signer: wallet.signer,
      spendingKeys: wallet.spendingKeys,
      notes,
      setNotes,
      addLog,
      setStatus,
    });

  useEffect(() => {
    setPendingSpentCommitments(new Set());
    setPendingBalanceValidationCommitments(new Set());
    setPrivateBalanceSyncing(false);
  }, [wallet.address, wallet.connected]);

  useEffect(() => {
    if (crossChainDestinationChain !== BRIDGE_DESTINATION_CHAIN) {
      setCrossChainDestinationChain(BRIDGE_DESTINATION_CHAIN);
    }
    if (
      !bridgeableTokens.some(
        (token) =>
          token.address.toLowerCase() ===
          crossChainInputToken.address.toLowerCase(),
      )
    ) {
      setCrossChainInputToken(bridgeableTokens[0] || DEFAULT_BRIDGE_TOKEN);
    }
  }, [
    bridgeableTokens,
    crossChainDestinationChain,
    crossChainInputToken.address,
  ]);

  useBaseSwapQuote({
    notes,
    locked: loading,
    refreshKey: swapQuoteRefreshKey,
    enabled:
      EVM_CONFIG.PRIVATE_SWAP_ENABLED &&
      activeTab === "swap" &&
      wallet.connected,
    swapAmount,
    swapInputToken,
    swapOutputToken,
    swapSlippageBps,
    setSwapQuote,
    setSwapQuoteLoading,
    setSwapQuoteError,
  });

  const { handleSwap } = useBaseSwapOperation({
    signer: wallet.signer,
    spendingKeys: wallet.spendingKeys,
    notes,
    addLog,
    setStatus,
    setLoading: setLoading,
  });
  const { handleBridge, handleBridgeCancel } = useBaseBridgeOperation({
    signer: wallet.signer,
    spendingKeys: wallet.spendingKeys,
    notes,
    addLog,
    setStatus,
    setLoading,
  });
  const crossChainExecutionDestinationRecipient =
    crossChainPublicRecipientAddress || "";
  // DLN expects the destination-chain order authority to be user-controlled.
  // For the public Solana v1 route, the recipient pubkey is the correct authority.
  const crossChainExecutionDestinationAuthority =
    crossChainPublicRecipientAddress || "";

  useEffect(() => {
    let active = true;
    const resolveDestination = async () => {
      const raw = crossChainDestinationAddress.trim();
      if (!raw) {
        setCrossChainDestinationResolutionError(null);
        setCrossChainPublicRecipientAddress(null);
        setCrossChainDestinationResolutionLoading(false);
        return;
      }
      setCrossChainDestinationResolutionLoading(true);
      setCrossChainDestinationResolutionError(null);
      try {
        // The cross-chain tab bridges to a PUBLIC destination wallet only
        // (`crossChainRecipientMode` is fixed to "public"), so the handle and
        // private-address branches that used to follow were unreachable.
        // Bridging to a public Solana address stays supported — it goes
        // through DLN and never depended on the retired Solana app.
        const isSolanaDestination = crossChainDestinationChain === "solana";
        const normalized = raw.trim();
        const validPublicRecipient = isSolanaDestination
          ? isLikelySolanaPubkey(normalized)
          : ethers.isAddress(normalized);
        if (!validPublicRecipient) {
          throw new Error(
            isSolanaDestination
              ? "Invalid Solana public recipient address"
              : "Invalid EVM public recipient address",
          );
        }
        if (!active) return;
        setCrossChainPublicRecipientAddress(normalized);
      } catch (err) {
        if (!active) return;
        setCrossChainPublicRecipientAddress(null);
        setCrossChainDestinationResolutionError(
          err instanceof Error ? err.message : String(err),
        );
      } finally {
        if (active) setCrossChainDestinationResolutionLoading(false);
      }
    };

    void resolveDestination();
    return () => {
      active = false;
    };
  }, [
    crossChainDestinationAddress,
    crossChainDestinationChain,
    crossChainRecipientMode,
  ]);

  const crossChainQuoteRequestKey = useMemo(() => {
    const normalizedDestinationRecipient =
      crossChainExecutionDestinationRecipient.trim();
    const normalizedDestinationAuthority =
      crossChainExecutionDestinationAuthority.trim();
    const normalizedSourceAuthority = (wallet.address || "").trim();
    const normalizedDestinationToken = crossChainDestinationTokenAddress.trim();
    if (
      !normalizedDestinationRecipient ||
      !normalizedDestinationAuthority ||
      !normalizedSourceAuthority ||
      !normalizedDestinationToken
    ) {
      return "";
    }
    if (
      crossChainSourceAmountBase === null ||
      crossChainSourceAmountBase <= 0n
    ) {
      return "";
    }
    try {
      return JSON.stringify({
        sourceChainId: EVM_CONFIG.CHAIN_ID,
        sourceToken: crossChainInputToken.address.toLowerCase(),
        sourceAmount: crossChainSourceAmountBase.toString(),
        destinationChainId: BRIDGE_DESTINATION_CHAIN_ID,
        destinationToken: normalizedDestinationToken.toLowerCase(),
        destinationRecipient: normalizedDestinationRecipient,
        sourceAuthority: normalizedSourceAuthority.toLowerCase(),
        destinationAuthority: normalizedDestinationAuthority,
      });
    } catch {
      return "";
    }
  }, [
    crossChainExecutionDestinationAuthority,
    crossChainExecutionDestinationRecipient,
    crossChainDestinationTokenAddress,
    crossChainInputToken.address,
    crossChainSourceAmountBase,
    wallet.address,
  ]);

  useEffect(() => {
    setCrossChainOrderStatus(null);
  }, [crossChainQuoteRequestKey]);

  useBaseBridgeQuote({
    enabled:
      activeTab === "crosschain" &&
      !anyPrivateBridgeDeployment() &&
      wallet.connected &&
      crossChainRecipientMode === "public" &&
      !crossChainDestinationResolutionLoading &&
      !crossChainDestinationResolutionError,
    requestKey: crossChainQuoteRequestKey,
    sourceChainId: EVM_CONFIG.CHAIN_ID,
    sourceToken: {
      address: crossChainInputToken.address,
      decimals: crossChainInputToken.decimals,
    },
    sourceAmount: crossChainSourceAmount,
    destinationChainId: BRIDGE_DESTINATION_CHAIN_ID,
    destinationToken: { address: crossChainDestinationTokenAddress },
    destinationRecipient: crossChainExecutionDestinationRecipient,
    sourceAuthority: wallet.address,
    destinationAuthority: crossChainExecutionDestinationAuthority,
    destinationPrivateRecipient: null,
    setBridgeQuote: setCrossChainQuote,
    setBridgeQuoteLoading: setCrossChainQuoteLoading,
    setBridgeQuoteError: setCrossChainQuoteError,
  });
  const crossChainQuoteReady =
    !!crossChainQuote?.tx &&
    crossChainQuote?.quote_request_key === crossChainQuoteRequestKey &&
    !crossChainQuoteLoading &&
    !crossChainQuoteError;

  const {
    claimedHandle,
    handleInput,
    setHandleInput,
    handleStatus,
    checkHandleAvailability,
    registerHandle,
    copyHandle,
    handlesSupported,
    busy: handleBusy,
    registering: handleRegistering,
    networkLabel: handleNetworkLabelHere,
    claimedDetails: claimedHandleDetails,
    canShareAcrossNetworks: canShareHandleAcrossNetworks,
    suggestedHandle,
    findOwnedHandle,
    updateClaimedHandle,
  } = useStealthHandleRegistration({
    signer: wallet.signer,
    proxyAuthReady,
    spendingKeys: wallet.spendingKeys,
    derivationSignatureB64Ref,
    ensureProxyAuthToken,
    addLog,
  });
  const {
    ownedNftOptions,
    discoveringNfts,
    nftDiscoveryError,
    selectedOwnedDepositNftIdx,
    setSelectedOwnedDepositNftIdx,
    selectedOwnedSendNftIdx,
    setSelectedOwnedSendNftIdx,
    discoverWalletNfts,
  } = useWalletNftDiscovery({
    connected: wallet.connected,
    signer: wallet.signer,
    addLog,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isPaymentMode) return;
    if (paymentLinkError) return;
    const params = new URLSearchParams(window.location.search);
    const amount = params.get("amount");
    const recipient = params.get("vault");
    const tokenAddress = params.get("token");
    const anyToken = params.get("anyToken") === "1";
    if (amount) {
      setDepositAmount(amount);
      setPaymentAmountLocked(true);
    }
    if (recipient) {
      setDepositRecipient(recipient);
      setPaymentRecipientLocked(true);
    }
    if (tokenAddress && privateTokenPool()) {
      setPaymentMintLocked(true);
      setPaymentTokenPending(true);
      void resolveSupportedToken(tokenAddress)
        .then((token) => {
          setSelectedDepositToken(token);
          setUseCustomDepositNft(false);
          setPaymentTokenError(false);
        })
        .catch(() => {
          setPaymentTokenError(true);
          setStatus({
            type: "error",
            message:
              "This payment link uses a token that is not supported or currently available.",
          });
        })
        .finally(() => setPaymentTokenPending(false));
    } else if (tokenAddress) {
      const match = BASE_PAYMENT_TOKEN_OPTIONS.find(
        (t) => t.address.toLowerCase() === tokenAddress.toLowerCase(),
      );
      if (match) {
        if (match.address.startsWith("nft:any:")) {
          setSelectedDepositToken(match);
          setUseCustomDepositNft(true);
          setCustomDepositNftType(
            match.tokenType === "erc1155" ? "erc1155" : "erc721",
          );
          setCustomDepositNftAddress("");
          setDepositTokenSubID("");
        } else {
          setUseCustomDepositNft(false);
          setSelectedDepositToken(match);
        }
        setPaymentMintLocked(true);
      } else {
        setPaymentMintLocked(true);
        setPaymentTokenPending(true);
        void resolveSupportedToken(tokenAddress)
          .then((token) => {
            if (!privateTokenPool() && usesPrivateFees() && !token.isNative)
              throw Error("Token support unavailable");
            setSelectedDepositToken(token);
            setUseCustomDepositNft(false);
          })
          .catch(() => {
            setPaymentTokenError(true);
            setStatus({
              type: "error",
              message:
                "This payment link's token could not be loaded. Check its contract address and network.",
            });
          })
          .finally(() => setPaymentTokenPending(false));
      }
    } else if (amount && !anyToken) {
      // Backward compatibility: old links may omit token for native ETH.
      setPaymentMintLocked(true);
    } else if (anyToken) {
      setPaymentMintLocked(false);
    }
    setActiveTab("shield");
  }, [isPaymentMode, paymentLinkError, resolveSupportedToken]);

  const fetchWalletBalance = useCallback(async () => {
    const isCurrent = walletSyncGuardRef.current.capture(walletSyncScope);
    if (!wallet.connected || !wallet.signer) {
      setWalletBalanceEth(null);
      return;
    }
    try {
      const address = await wallet.signer.getAddress();
      const provider = wallet.signer.provider;
      if (!provider) return;
      const balanceWei = await withDeadline<bigint>(
        () => provider.getBalance(address),
        "Wallet balance refresh timed out.",
      );
      if (!isCurrent()) return;
      setWalletBalanceEth(Number(ethers.formatEther(balanceWei)));
    } catch {
      if (isCurrent()) setWalletBalanceEth(null);
    }
  }, [wallet.connected, wallet.signer, walletSyncScope]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const map = new Map<string, bigint>();
      // Flat JoinSplit token domains are pool- and tree-scoped.  A legacy
      // address-only token hash can never be used to classify those notes.
      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
        if (!cancelled) setTokenHashMap(map);
        return;
      }
      for (const token of BASE_SEPOLIA_TOKENS) {
        const hash = await deriveTokenHash(
          token.address,
          baseTokenHashType(token),
          normalizeTokenSubID(token.tokenSubID),
        );
        map.set(token.address.toLowerCase(), hash);
      }
      if (!cancelled) setTokenHashMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { balance: walletTokenBalance, refresh: fetchTokenBalance } =
    useWalletTokenBalance(
      wallet.connected ? wallet.address : null,
      Number(chainId),
      wallet.signer,
      activeDepositToken,
    );

  useEffect(() => {
    setDepositTokenSubID(selectedDepositToken.tokenSubID ?? "");
  }, [selectedDepositToken]);

  useEffect(() => {
    setSendTokenSubID(selectedSendToken.tokenSubID ?? "");
  }, [selectedSendToken]);

  const approveTokenSpend = useCallback(
    async (token: TokenInfo, amount: bigint): Promise<boolean> => {
      if (token.isNative || !wallet.signer) return true;
      const contractAddress = usesPrivateFees()
        ? privatePoolForToken(token.address)
        : getActiveBaseShieldedPoolAddress();
      const vaultAddress =
        EVM_CONFIG.CONTRACTS.NFT_PRIVACY_VAULT &&
        ethers.isAddress(EVM_CONFIG.CONTRACTS.NFT_PRIVACY_VAULT)
          ? ethers.getAddress(EVM_CONFIG.CONTRACTS.NFT_PRIVACY_VAULT)
          : null;
      const spender =
        token.isWrappedNft && vaultAddress ? vaultAddress : contractAddress;
      const owner = await wallet.signer.getAddress();

      if (token.tokenType === "erc721") {
        const subId = (token.tokenSubID ?? "").trim();
        if (!subId) {
          addLog("Approval failed: Missing ERC721 token ID", "error");
          return false;
        }
        const erc721 = new ethers.Contract(
          token.address,
          [
            "function getApproved(uint256) view returns (address)",
            "function isApprovedForAll(address,address) view returns (bool)",
            "function approve(address,uint256)",
          ],
          wallet.signer,
        );
        const tokenId = BigInt(subId);
        const [approved, approvedForAll] = await Promise.all([
          erc721.getApproved(tokenId),
          erc721.isApprovedForAll(owner, spender),
        ]);
        if (
          approvedForAll ||
          String(approved).toLowerCase() === spender.toLowerCase()
        )
          return true;
        flushSync(() => setApproving(true));
        addLog(`Approving ${token.symbol} token #${subId}...`, "info");
        await waitForNextPaint();
        try {
          const tx = await erc721.approve(spender, tokenId);
          await tx.wait();
          addLog(`${token.symbol} token #${subId} approved`, "success");
          return true;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          addLog(`Approval failed: ${msg}`, "error");
          return false;
        } finally {
          setApproving(false);
        }
      }

      if (token.tokenType === "erc1155") {
        const erc1155 = new ethers.Contract(
          token.address,
          [
            "function isApprovedForAll(address,address) view returns (bool)",
            "function setApprovalForAll(address,bool)",
          ],
          wallet.signer,
        );
        const approvedForAll = await erc1155.isApprovedForAll(owner, spender);
        if (approvedForAll) return true;
        flushSync(() => setApproving(true));
        addLog(`Approving ${token.symbol} collection...`, "info");
        await waitForNextPaint();
        try {
          const tx = await erc1155.setApprovalForAll(spender, true);
          await tx.wait();
          addLog(`${token.symbol} collection approved`, "success");
          return true;
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          addLog(`Approval failed: ${msg}`, "error");
          return false;
        } finally {
          setApproving(false);
        }
      }

      const erc20 = new ethers.Contract(
        token.address,
        [
          "function allowance(address,address) view returns (uint256)",
          "function approve(address,uint256) returns (bool)",
        ],
        wallet.signer,
      );
      const current: bigint = await erc20.allowance(owner, spender);
      if (current >= amount) return true;

      flushSync(() => setApproving(true));
      addLog(`Approving ${token.symbol} spend...`, "info");
      await waitForNextPaint();
      try {
        if (current > 0n) {
          // Tokens such as USDT require resetting an existing allowance first.
          const reset = await erc20.approve(spender, 0n);
          await reset.wait();
        }
        const tx = await erc20.approve(spender, amount);
        await tx.wait();
        addLog(`${token.symbol} approved`, "success");
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        addLog(`Approval failed: ${msg}`, "error");
        return false;
      } finally {
        setApproving(false);
      }
    },
    [wallet.signer, addLog, waitForNextPaint],
  );

  const prepareInboxSnapshot = useCallback(
    async (head?: unknown) =>
      withDeadline(async (signal) => {
        const isCurrent = walletSyncGuardRef.current.capture(walletSyncScope);
        if (!wallet.spendingKeys || !wallet.connected || !isCurrent()) return;
        const recipientKey = await hashMpkHex(
          formatMpkHex(wallet.spendingKeys.mpk),
        );
        // Incremental path: with a recent full validation on record and a verified
        // head of the same index generation, only the new inbox lane entries and
        // the chain feed since the stored cursors are downloaded. Any doubt falls
        // back to the full download and canonical validation below.
        const headReading = (head ?? null) as SyncHeadReading | null;
        const headCursors = (headReading?.cursors ??
          null) as SyncHeadCursors | null;
        const indexSecret = derivationSignatureB64Ref.current;
        const indexScope = `stealthos-sync-index-v1:${EVM_CONFIG.CHAIN_ID}:${recipientKey}`;
        const indexKey = `${indexScope}:${wallet.address.toLowerCase()}`;
        let savedIndex: SyncIndexState | null = null;
        if (indexSecret && EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
          try {
            const loaded = await loadEncryptedJsonCache<SyncIndexState>(
              indexScope,
              indexKey,
              wallet.address,
              indexSecret,
            );
            if (isSyncIndexState(loaded)) savedIndex = loaded;
          } catch {
            savedIndex = null;
          }
        }
        const relayerPositions = Object.fromEntries(
          (
            (
              headReading?.cursors as {
                relayer?: { pool: string; position: number | null }[];
              } | null
            )?.relayer ?? []
          )
            .filter((entry) => typeof entry.position === "number")
            .map((entry) => [
              entry.pool.toLowerCase(),
              entry.position as number,
            ]),
        );
        const deltaAllowed =
          headReading &&
          headCursors &&
          savedIndex &&
          EVM_CONFIG.JOINSPLIT_V2.INCREMENTAL_SYNC &&
          EVM_CONFIG.JOINSPLIT_V2.ENABLED
            ? canApplyDelta({
                index: savedIndex,
                head: headCursors,
                authoritative: headReading.authoritative,
              })
            : { ok: false as const, reason: "no head reading" };
        if (!headReading)
          addLog("Full validation: no sync head reading for this pass", "info");
        else if (!savedIndex)
          addLog(
            "Full validation: no local note index yet; it is written after this validation",
            "info",
          );
        if (deltaAllowed.ok && savedIndex && headCursors && headReading) {
          try {
            const index = savedIndex;
            const laneHeaders = async (lane: "pending" | "protected") =>
              authHeadersForPath(`/api/v1/notes/${lane}/${recipientKey}`, {
                expectedRecipientKey: recipientKey,
              });
            const chainHeaders = await authHeadersForPath(
              "/api/v1/sync/chain",
              { interactive: false },
            );
            if (!isCurrent())
              throw new Error("Wallet changed during inbox sync");
            const relayerChanged = Object.entries(relayerPositions).some(
              ([pool, position]) => (index.relayer ?? {})[pool] !== position,
            );
            const [pendingNotes, protectedNotes, chain, feeNotes] =
              await Promise.all([
                fetchInboxLane({
                  baseUrl: NOTES_API_URL,
                  recipientKey,
                  headers: await laneHeaders("pending"),
                  lane: "pending",
                  cursor: index.lanes.pending,
                  signal,
                }),
                fetchInboxLane({
                  baseUrl: NOTES_API_URL,
                  recipientKey,
                  headers: await laneHeaders("protected"),
                  lane: "protected",
                  cursor: index.lanes.protected,
                  signal,
                }),
                fetchChainDelta({
                  cursor: index.chain,
                  fetchPage: (cursor) =>
                    fetchChainPage({
                      baseUrl: NOTES_API_URL,
                      headers: chainHeaders,
                      cursor,
                      expectedGeneration: index.generation,
                      signal,
                    }),
                }),
                relayerChanged
                  ? fetchPrivateFeeRecovery({
                      address: wallet.address,
                      cacheSecret: indexSecret,
                      recipientKey,
                      signal,
                    })
                  : Promise.resolve([] as any[]),
              ]);
            if (!isCurrent()) return;
            addLog(
              `Incremental sync: ${pendingNotes.length + protectedNotes.length + feeNotes.length} new deliveries, ${chain.leaves.length} leaves, ${chain.nullifiers.length} nullifiers`,
              "info",
            );
            return {
              scope: walletSyncScope,
              notes: [...pendingNotes, ...protectedNotes, ...feeNotes],
              chainSnapshot: undefined,
              delta: {
                chain,
                head: headCursors,
                index,
                relayer: relayerPositions,
                safeBlock: headReading.chain.indexedBlock ?? 0,
                safeBlockHash: headReading.chain.indexedBlockHash ?? "",
              },
              fullSync: undefined,
            };
          } catch (error) {
            if (!isCurrent()) return;
            addLog(
              `Incremental sync unavailable (${error instanceof Error ? error.message : String(error)}); validating in full`,
              "info",
            );
          }
        } else if (savedIndex && headReading && !deltaAllowed.ok) {
          addLog(`Full validation: ${deltaAllowed.reason}`, "info");
        }
        const fetchNotes = async () => {
          const headers = await authHeadersForPath("/api/v1/notes", {
            expectedRecipientKey: recipientKey,
          });
          if (!isCurrent()) throw new Error("Wallet changed during inbox sync");
          return fetch(
            `${NOTES_API_URL}/api/v1/notes/${recipientKey}?includeSpent=1`,
            {
              headers: { "Content-Type": "application/json", ...headers },
              signal,
            },
          );
        };
        const [notes, protectedNotes, feeNotes, chainSnapshot] =
          await Promise.all([
            (async () => {
              let response = await fetchNotes();
              if (
                isCurrent() &&
                (response.status === 401 || response.status === 403)
              ) {
                clearProxyAuthToken();
                response = await fetchNotes();
              }
              if (!response.ok)
                throw new Error(`Inbox fetch failed (${response.status})`);
              const result = await response.json();
              if (!Array.isArray(result.notes))
                throw new Error("Malformed inbox response");
              return result.notes as any[];
            })(),
            (async () => {
              if (!EVM_CONFIG.JOINSPLIT_V2.ENABLED) return [];
              try {
                const headers = await authHeadersForPath(
                  `/api/v1/notes/protected/${recipientKey}`,
                  { expectedRecipientKey: recipientKey },
                );
                if (!isCurrent()) return [];
                return await fetchProtectedFlatInbox({
                  baseUrl: NOTES_API_URL,
                  recipientKey,
                  headers,
                });
              } catch {
                if (isCurrent())
                  addLog(
                    "Protected recovery export is unavailable; inbox recovery may be incomplete",
                    "warning",
                  );
                return [];
              }
            })(),
            fetchPrivateFeeRecovery({
              address: wallet.address,
              cacheSecret: derivationSignatureB64Ref.current || "",
              recipientKey,
              signal,
            }),
            EVM_CONFIG.JOINSPLIT_V2.INCREMENTAL_SYNC &&
            EVM_CONFIG.JOINSPLIT_V2.ENABLED
              ? prepareFlatBalanceSnapshot({
                  provider: getBaseReadProvider(),
                  notes: [...notesRef.current],
                  signal,
                })
              : Promise.resolve(undefined),
          ]);
        if (!isCurrent()) return;
        return {
          scope: walletSyncScope,
          notes: [...notes, ...protectedNotes, ...feeNotes],
          chainSnapshot,
          delta: undefined,
          fullSync: {
            head: headCursors,
            authoritative: !!headReading?.authoritative && !!headCursors?.chain,
            index: savedIndex,
            relayer: relayerPositions,
          },
        };
      }, "Balance service is taking too long. Please try again."),
    [
      wallet.address,
      wallet.spendingKeys,
      wallet.connected,
      walletSyncScope,
      authHeadersForPath,
      NOTES_API_URL,
      clearProxyAuthToken,
      addLog,
    ],
  );

  /** This wallet's encrypted local note index on this chain, if any. */
  const loadLocalSyncIndex = useCallback(
    async (recipientKey: string): Promise<SyncIndexState | null> => {
      const secret = derivationSignatureB64Ref.current;
      if (!secret || !wallet.address || !EVM_CONFIG.JOINSPLIT_V2.ENABLED)
        return null;
      const scope = `stealthos-sync-index-v1:${EVM_CONFIG.CHAIN_ID}:${recipientKey}`;
      try {
        const loaded = await loadEncryptedJsonCache<SyncIndexState>(
          scope,
          `${scope}:${wallet.address.toLowerCase()}`,
          wallet.address,
          secret,
        );
        return isSyncIndexState(loaded) ? loaded : null;
      } catch {
        return null;
      }
    },
    [wallet.address],
  );

  // One cheap read of every server cursor that can move this wallet’s private
  // balance. Equal cursors mean the inbox download and chain validation are
  // skipped; any failure or unverified index means a full refresh instead.
  const checkSyncHead = useCallback(async () => {
    if (
      !wallet.spendingKeys ||
      !wallet.connected ||
      !wallet.address ||
      !EVM_CONFIG.JOINSPLIT_V2.INCREMENTAL_SYNC ||
      !EVM_CONFIG.JOINSPLIT_V2.ENABLED
    )
      return null;
    const isCurrent = walletSyncGuardRef.current.capture(walletSyncScope);
    const address = wallet.address;
    const recipientKey = await hashMpkHex(
      formatMpkHex(wallet.spendingKeys.mpk),
    );
    const cacheSecret = derivationSignatureB64Ref.current;
    const scope = `stealthos-sync-head-v1:${EVM_CONFIG.CHAIN_ID}:${recipientKey}`;
    const storageKey = `${scope}:${address.toLowerCase()}`;
    const check = await withDeadline(
      (signal) =>
        evaluateSyncHead({
          signal,
          read: async () => {
            const headers = await authHeadersForPath("/api/v1/sync/head", {
              expectedRecipientKey: recipientKey,
            });
            if (!isCurrent()) throw new Error("Wallet changed during sync");
            const dataHead = fetch(
              `${NOTES_API_URL}/api/v1/sync/head/${recipientKey}`,
              {
                headers: { "Content-Type": "application/json", ...headers },
                signal,
              },
            ).then(async (response) => {
              if (!response.ok)
                throw new Error(`Sync head fetch failed (${response.status})`);
              return response.json();
            });
            const relayerHeads = usesPrivateFees()
              ? Promise.all(
                  privatePools().map(async (pool) => {
                    const head = await requestPrivateFees<{
                      success: boolean;
                      epoch: string;
                      position: number;
                      count: number;
                    }>("recover-head", {}, { pool, signal });
                    return { pool, ...head };
                  }),
                )
              : Promise.resolve([]);
            const [data, relayer] = await Promise.all([dataHead, relayerHeads]);
            return syncHeadReadingFromResponses(data, relayer);
          },
          load: () =>
            cacheSecret
              ? loadEncryptedJsonCache<SyncHeadRecord>(
                  scope,
                  storageKey,
                  address,
                  cacheSecret,
                )
              : Promise.resolve(null),
          save: (record) =>
            cacheSecret && isCurrent()
              ? saveEncryptedJsonCache(
                  scope,
                  storageKey,
                  address,
                  record,
                  cacheSecret,
                )
              : Promise.resolve(),
        }),
      "Sync check is taking too long.",
      undefined,
      8_000,
    );
    // Nothing was ever delivered and nothing is held here: a new wallet has no
    // note to find, whatever this device's local index says.
    const empty =
      check.empty &&
      !notesRef.current.some(
        (stored) => stored.note.protocol === "flat-joinsplit-v2",
      );
    if (empty)
      addLog(
        "Nothing to sync yet: no deliveries, journal records or fee recovery for this wallet",
        "info",
      );
    // An unchanged head skips the chain only through the local note index
    // committed at that head; one full pass rebuilds an index that cannot
    // vouch for it (none yet, saved at another head, or by an earlier version).
    let unchanged = check.unchanged;
    if (unchanged && !empty) {
      unchanged = syncIndexVouchesForHead(
        await loadLocalSyncIndex(recipientKey),
        check.fingerprint,
      );
      if (!unchanged)
        addLog(
          "Revalidating: the local note index does not match this sync head yet; rebuilding it",
          "info",
        );
    }
    return { ...check, unchanged, empty };
  }, [
    wallet.address,
    wallet.spendingKeys,
    wallet.connected,
    walletSyncScope,
    authHeadersForPath,
    NOTES_API_URL,
    addLog,
    loadLocalSyncIndex,
  ]);

  const syncInboxNotes = useCallback(
    async (prepared?: Awaited<ReturnType<typeof prepareInboxSnapshot>>) => {
      if (!wallet.spendingKeys || !wallet.connected) return;
      const operation = walletSyncGuardRef.current.begin(walletSyncScope);
      if (!operation) return;
      const scopeCurrent = operation.isCurrent;
      setPrivateBalanceSyncing(true);
      try {
        return await withDeadline(
          async (signal) => {
            const isCurrent = () => scopeCurrent() && !signal.aborted;
            const initialLocalNotes = [...notesRef.current];
            const initialLocalCommitments = new Set(
              initialLocalNotes.map((stored) =>
                stored.note.commitment.toLowerCase(),
              ),
            );
            const cacheSecret = derivationSignatureB64Ref.current;
            setPrivateBalanceSyncing((current) =>
              isCurrent() ? true : current,
            );
            try {
              const mpkHex = formatMpkHex(wallet.spendingKeys.mpk);
              const recipientKey = await hashMpkHex(mpkHex);
              if (!isCurrent()) return;
              // Candidates, not a single key: notes stored before the inbox was
              // re-keyed off the viewing key are sealed to the older material.
              const viewingKeyHex = getInboxViewingKeyCandidates(
                wallet.spendingKeys.spendingKey,
              );
              const inboxSecretKey = getInboxSecretKeyCandidates(
                wallet.spendingKeys.spendingKey,
              );
              const provider = EVM_CONFIG.JOINSPLIT_V2.ENABLED
                ? getBaseReadProvider()
                : wallet.signer?.provider;
              if (!provider) return;
              const contractAddress = getActiveBaseShieldedPoolAddress();
              const merkleApiCandidates = [
                EVM_CONFIG.PROVER_URL,
                EVM_CONFIG.PROVER_DIRECT_URL,
                EVM_CONFIG.NOVA_PROVER_URL,
              ]
                .map((u) => (u || "").replace(/\/+$/, ""))
                .filter(Boolean)
                // Browser calls must stay on proxy routes so X-API-Key remains server-side.
                .filter((u) => u.startsWith("/api/proxy/"));
              const pool = new ethers.Contract(
                contractAddress,
                ["function nullifierUsed(bytes32) view returns (bool)"],
                provider,
              );
              const findLeafIndexByMerkle = async (
                commitment: string,
                treeIndex?: number,
                treeKey?: string,
              ): Promise<number | null> => {
                for (const base of merkleApiCandidates) {
                  try {
                    const response = await fetch(
                      `${base}/api/v1/public/merkle/find`,
                      {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                          commitment,
                          ...(treeIndex !== undefined
                            ? { tree_index: treeIndex }
                            : {}),
                          ...(treeKey ? { tree_pda: treeKey } : {}),
                        }),
                      },
                    );
                    if (!response.ok) continue;
                    const data = (await response.json()) as {
                      found?: boolean;
                      leaf_index?: number;
                      leafIndex?: number;
                    };
                    if (!data.found) continue;
                    const idx = data.leaf_index ?? data.leafIndex;
                    if (typeof idx === "number" && idx >= 0) return idx;
                  } catch {
                    // try next candidate
                  }
                }
                return null;
              };
              const markSpent = async (commitment: string) => {
                try {
                  const recordId = await buildInboxNoteRecordId(
                    recipientKey,
                    commitment,
                  );
                  const authHeaders = await authHeadersForPath(
                    "/api/v1/notes/spent",
                    {
                      expectedRecipientKey: recipientKey,
                    },
                  );
                  if (!isCurrent()) return;
                  await fetch(`${NOTES_API_URL}/api/v1/notes/spent`, {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                      ...authHeaders,
                    },
                    body: JSON.stringify({
                      recipient: recipientKey,
                      recordId,
                      commitment,
                    }),
                  });
                } catch {
                  // best-effort
                }
              };
              const snapshot = prepared ?? (await prepareInboxSnapshot());
              if (
                !isCurrent() ||
                !snapshot ||
                snapshot.scope !== walletSyncScope
              )
                return;
              const encryptedNotes = snapshot.notes;
              if (
                encryptedNotes.length === 0 &&
                !EVM_CONFIG.JOINSPLIT_V2.ENABLED
              ) {
                const pendingInboxNotes = await readEncryptedPendingInboxNotes(
                  wallet.address,
                  cacheSecret,
                );
                const pendingInboxCommitments = new Set(
                  pendingInboxNotes.map((item) =>
                    item.commitment.toLowerCase(),
                  ),
                );
                setNotes((currentNotes) => {
                  if (!isCurrent()) return currentNotes;
                  if (pendingInboxCommitments.size === 0) return [];
                  return currentNotes.filter((local) => {
                    const key = String(
                      local?.note?.commitment || "",
                    ).toLowerCase();
                    return (
                      key && pendingInboxCommitments.has(key) && !local.spent
                    );
                  });
                });
                setPendingBalanceValidationCommitments((current) =>
                  isCurrent() ? new Set() : current,
                );
                return;
              }

              const commitmentLeaf = new Map<
                string,
                {
                  leafIndex: number;
                  spent: boolean;
                  treeIndex?: number;
                  treeKey?: string;
                }
              >();
              // Flat-v2 authenticates locations and spent state from the chain below.
              // Legacy index metadata is not used in that path, so do not fetch it.
              if (!EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
                const commitmentKeys = Array.from(
                  new Set(
                    [recipientKey, wallet.address.toLowerCase()].filter(
                      Boolean,
                    ),
                  ),
                );
                const commitmentAuthHeaders = await authHeadersForPath(
                  "/api/v1/commitments",
                  {
                    expectedRecipientKey: recipientKey,
                  },
                );
                if (!isCurrent()) return;
                const commitmentResults = await Promise.all(
                  commitmentKeys.map((keyToFetch) =>
                    fetchPrivateCommitments({
                      baseUrl: NOTES_API_URL,
                      addressKey: keyToFetch,
                      headers: commitmentAuthHeaders,
                      decryptContext: {
                        viewingKeyHex,
                        legacyMpkHex: mpkHex,
                        inboxSecretKey,
                      },
                      throwOnHttpError: false,
                    }),
                  ),
                );
                for (const commitments of commitmentResults) {
                  for (const c of commitments) {
                    const key = String(c.commitment || "").toLowerCase();
                    const index = c.leafIndex ?? c.leaf_index;
                    const spent = Boolean(c.spent);
                    const treeIndex = c.treeIndex ?? c.tree_index;
                    const treeKey =
                      c.treeKey ?? c.tree_key ?? c.treePda ?? c.tree_pda;
                    if (key && typeof index === "number")
                      commitmentLeaf.set(key, {
                        leafIndex: index,
                        spent,
                        treeIndex:
                          typeof treeIndex === "number" ? treeIndex : undefined,
                        treeKey:
                          typeof treeKey === "string" ? treeKey : undefined,
                      });
                  }
                }
              }

              const decryptedNotes = await Promise.all(
                encryptedNotes.map(async (enc) => {
                  try {
                    const note = await decryptInboxNote(
                      enc,
                      viewingKeyHex,
                      mpkHex,
                      inboxSecretKey,
                    );
                    if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
                      const notePool = privatePools().find(
                        (pool) =>
                          pool.toLowerCase() ===
                          note.poolAddress?.toLowerCase(),
                      );
                      if (!notePool) throw Error("Unknown note pool");
                      await validateFlatInboxCandidate(
                        { enc, note },
                        {
                          chainId: EVM_CONFIG.CHAIN_ID,
                          poolAddress: notePool,
                          mpk: wallet.spendingKeys!.mpk,
                        },
                      );
                    }
                    return { enc, note };
                  } catch {
                    return null;
                  }
                }),
              );
              if (!isCurrent()) return;

              const imported: BaseStoredNote[] = [];
              const derivedHistoryEntries: Array<
                Omit<BaseHistoryEntry, "id" | "timestamp"> & {
                  timestamp?: number;
                }
              > = [];
              const spentCommitments = new Set<string>(pendingSpentCommitments);
              const validationCommitments = new Set<string>();
              for (const decrypted of decryptedNotes) {
                if (!decrypted) continue;
                const { enc, note } = decrypted;
                try {
                  if (
                    EVM_CONFIG.JOINSPLIT_V2.ENABLED !==
                    (note.protocol === "flat-joinsplit-v2" &&
                      note.schemaVersion === 2)
                  ) {
                    continue;
                  }
                  const commitment = note.commitment.startsWith("0x")
                    ? note.commitment
                    : `0x${note.commitment}`;
                  const commitmentKey = commitment.toLowerCase();
                  if (pendingSpentCommitments.has(commitmentKey)) {
                    spentCommitments.add(commitmentKey);
                    continue;
                  }
                  const commitmentMeta = commitmentLeaf.get(commitmentKey);
                  if (
                    !EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
                    commitmentMeta?.spent
                  ) {
                    void markSpent(commitment);
                    spentCommitments.add(commitmentKey);
                    continue;
                  }
                  const encTreeIndexRaw =
                    enc?.treeIndex ??
                    enc?.tree_index ??
                    enc?.note?.treeIndex ??
                    enc?.note?.tree_index;
                  const encTreeKeyRaw =
                    enc?.treeKey ??
                    enc?.tree_key ??
                    enc?.treePda ??
                    enc?.tree_pda ??
                    enc?.note?.treeKey ??
                    enc?.note?.tree_key ??
                    enc?.note?.treePda ??
                    enc?.note?.tree_pda;
                  const encTreeIndex = Number.isFinite(Number(encTreeIndexRaw))
                    ? Number(encTreeIndexRaw)
                    : undefined;
                  const encTreeKey =
                    typeof encTreeKeyRaw === "string"
                      ? encTreeKeyRaw
                      : undefined;
                  const resolvedTreeIndex = EVM_CONFIG.JOINSPLIT_V2.ENABLED
                    ? note.treeIndex
                    : (encTreeIndex ?? commitmentMeta?.treeIndex);
                  const resolvedTreeKey = EVM_CONFIG.JOINSPLIT_V2.ENABLED
                    ? note.treeKey
                    : (encTreeKey ?? commitmentMeta?.treeKey);
                  // enc.leafIndex may be at top level or nested under enc.note depending on API shape
                  const rawEncLeaf =
                    enc?.leafIndex ??
                    enc?.leaf_index ??
                    enc?.note?.leafIndex ??
                    enc?.note?.leaf_index;
                  const encLeafIndex =
                    rawEncLeaf != null ? Number(rawEncLeaf) : -1;
                  // Flat-v2 mailbox/index metadata is untrusted. It becomes spendable
                  // only after the finalized on-chain event scan below authenticates
                  // the commitment, tree, token domain, ownership, and nullifier.
                  const resolvedLeafIndex = EVM_CONFIG.JOINSPLIT_V2.ENABLED
                    ? -1
                    : (commitmentMeta?.leafIndex ??
                      (Number.isFinite(encLeafIndex) && encLeafIndex >= 0
                        ? encLeafIndex
                        : -1));
                  validationCommitments.add(commitmentKey);
                  imported.push({
                    note: {
                      ...note,
                      commitment,
                      leafIndex: resolvedLeafIndex,
                      treeIndex: resolvedTreeIndex,
                      treeKey: resolvedTreeKey,
                    },
                    timestamp: Date.now(),
                  });
                  const privateHistory = note.privateHistory;
                  if (privateHistory?.type && privateHistory.amount) {
                    derivedHistoryEntries.push({
                      type: privateHistory.type,
                      amount: privateHistory.amount,
                      symbol: privateHistory.symbol || "ETH",
                      memo: privateHistory.memo,
                      txHash: privateHistory.txHash,
                      timestamp: privateHistory.timestamp,
                    });
                  }
                } catch {
                  // ignore malformed/decrypt-failed notes
                }
              }
              const importedByCommitment = new Map<string, BaseStoredNote>();
              for (const incoming of imported) {
                const key = incoming.note.commitment.toLowerCase();
                if (spentCommitments.has(key)) continue;
                importedByCommitment.set(key, incoming);
              }
              const pendingInboxCommitments = new Set(
                (
                  await readEncryptedPendingInboxNotes(
                    wallet.address,
                    cacheSecret,
                  )
                ).map((item) => item.commitment.toLowerCase()),
              );
              if (!isCurrent()) return;
              // Authoritative note state remains backend inbox + engine/on-chain spent
              // checks, except for confirmed notes whose encrypted inbox write is still
              // queued for retry while Merkle indexing/storage catches up.
              setNotes((currentNotes) => {
                if (!isCurrent()) return currentNotes;
                // Inbox plaintext is only a candidate. Keep existing notes intact until
                // canonical validation has checked every duplicate independently.
                if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) return currentNotes;
                const nextByCommitment = new Map(importedByCommitment);
                for (const local of currentNotes) {
                  const key = String(
                    local?.note?.commitment || "",
                  ).toLowerCase();
                  if (
                    !key ||
                    spentCommitments.has(key) ||
                    nextByCommitment.has(key)
                  )
                    continue;
                  if (pendingInboxCommitments.has(key)) {
                    nextByCommitment.set(key, local);
                  }
                }
                return Array.from(nextByCommitment.values());
              });
              setPendingBalanceValidationCommitments((current) =>
                isCurrent() ? validationCommitments : current,
              );
              if (spentCommitments.size > 0) {
                addLog(
                  `Marked ${spentCommitments.size} spent note(s) from backend/on-chain sync`,
                  "info",
                );
              }
              if (derivedHistoryEntries.length > 0) {
                mergeHistoryEntries(derivedHistoryEntries);
                void Promise.allSettled(
                  derivedHistoryEntries.map(
                    ({ timestamp: _timestamp, ...entry }) =>
                      storeHistoryEvent(entry),
                  ),
                );
              }
              if (!isCurrent()) return;
              // A full validation reads every inbox note; only ones this
              // device didn't hold yet are news.
              const newlyImported = imported.filter(
                (stored) =>
                  !initialLocalCommitments.has(
                    stored.note.commitment.toLowerCase(),
                  ),
              ).length;
              if (newlyImported > 0) {
                addLog(
                  `Imported ${newlyImported} new note${newlyImported === 1 ? "" : "s"} from inbox`,
                  "success",
                );
              }

              return await (async () => {
                try {
                  if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
                    const candidates = [
                      ...initialLocalNotes,
                      ...imported,
                    ].filter(
                      (candidate) =>
                        candidate.note.protocol === "flat-joinsplit-v2",
                    );
                    const indexScope = `stealthos-sync-index-v1:${EVM_CONFIG.CHAIN_ID}:${recipientKey}`;
                    const indexKey = `${indexScope}:${wallet.address.toLowerCase()}`;
                    let nextIndex: SyncIndexState | null = null;
                    let authoritative:
                      | Awaited<
                          ReturnType<typeof synchronizeFlatJoinSplitV2Notes>
                        >
                      | undefined;
                    if (snapshot.delta) {
                      try {
                        const spendingKeys = wallet.spendingKeys!;
                        const applied = await applyIndexedSync({
                          index: snapshot.delta.index,
                          candidates,
                          delta: snapshot.delta.chain as SyncChainDelta,
                          head: snapshot.delta.head,
                          safeBlock: snapshot.delta.safeBlock,
                          safeBlockHash: snapshot.delta.safeBlockHash,
                          chainId: EVM_CONFIG.CHAIN_ID,
                          tokenHashFor: tokenHashReaderRef.current,
                          authenticate: ({
                            note,
                            poolAddress,
                            tokenAddress,
                            expectedTokenHash,
                          }) =>
                            assertFlatJoinSplitV2Note({
                              note,
                              chainId: EVM_CONFIG.CHAIN_ID,
                              poolAddress,
                              tokenAddress,
                              expectedTokenHash,
                              expectedMpk: spendingKeys.mpk,
                            }),
                          nullifierFor: (tokenHash, leafIndex) =>
                            computeNullifier(
                              spendingKeys.nullifyingKey,
                              tokenHash,
                              leafIndex,
                            ),
                          treeKeyFor: (pool, treeIndex) =>
                            flatTreeKey(EVM_CONFIG.CHAIN_ID, pool, treeIndex),
                          knownPools: privatePools(),
                        });
                        authoritative = applied.result;
                        nextIndex = {
                          ...applied.index,
                          relayer: snapshot.delta.relayer,
                          headFingerprint: syncHeadFingerprint(
                            snapshot.delta.head,
                          ),
                        };
                      } catch (error) {
                        if (!isCurrent() || signal.aborted) throw error;
                        // A failed incremental validation never fails the
                        // refresh: the full validation below reads the chain
                        // itself, and a later head change retries the delta.
                        addLog(
                          `Incremental validation unavailable (${
                            (error as { shortMessage?: string })
                              ?.shortMessage ||
                            (error instanceof Error
                              ? error.message
                              : String(error))
                          }); validating in full`,
                          "warning",
                        );
                      }
                    }
                    if (!authoritative) {
                      authoritative = await synchronizeFlatJoinSplitV2Notes({
                        provider,
                        spendingKeys: wallet.spendingKeys!,
                        notes: candidates,
                        signal,
                        preparedSnapshot: snapshot.chainSnapshot,
                      });
                      if (
                        snapshot.fullSync?.authoritative &&
                        snapshot.fullSync.head
                      ) {
                        nextIndex = {
                          ...syncIndexAfterFullValidation({
                            previous: snapshot.fullSync.index,
                            result: authoritative,
                            head: snapshot.fullSync.head,
                            nullifiers:
                              snapshot.fullSync.index?.nullifiers ?? {},
                          }),
                          relayer: snapshot.fullSync.relayer,
                          headFingerprint: syncHeadFingerprint(
                            snapshot.fullSync.head,
                          ),
                        };
                      }
                    }
                    if (!isCurrent()) return;
                    if (nextIndex && cacheSecret) {
                      const persisted = nextIndex;
                      void saveEncryptedJsonCache(
                        indexScope,
                        indexKey,
                        wallet.address,
                        persisted,
                        cacheSecret,
                      ).catch(() => undefined);
                    }
                    // Canonical chain validation is complete. Publish it before the
                    // recovery acknowledgement, which can be retried independently.
                    setPendingBalanceValidationCommitments((current) =>
                      isCurrent() ? new Set(authoritative.pending) : current,
                    );
                    const auditedCommitments = new Set(
                      candidates.map((candidate) =>
                        candidate.note.commitment.toLowerCase(),
                      ),
                    );
                    setNotes((currentNotes) =>
                      mergeValidatedFlatInboxNotes(
                        currentNotes,
                        authoritative,
                        auditedCommitments,
                        initialLocalCommitments,
                        isCurrent,
                      ),
                    );
                    void (async () => {
                      try {
                        const protectionHeaders = await authHeadersForPath(
                          "/api/v1/notes/promote",
                          { expectedRecipientKey: recipientKey },
                        );
                        if (!isCurrent()) return;
                        await protectValidatedFlatInbox({
                          baseUrl: NOTES_API_URL,
                          recipientKey,
                          headers: protectionHeaders,
                          candidates: decryptedNotes.filter(
                            (
                              candidate,
                            ): candidate is NonNullable<typeof candidate> =>
                              candidate !== null &&
                              candidate.note.poolAddress?.toLowerCase() ===
                                EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT.toLowerCase(),
                          ),
                          validated: authoritative.validated,
                          isCurrent,
                          signal: AbortSignal.timeout(20_000),
                          scope: {
                            chainId: EVM_CONFIG.CHAIN_ID,
                            poolAddress:
                              EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT,
                            mpk: wallet.spendingKeys!.mpk,
                          },
                        });
                      } catch {
                        if (!isCurrent()) return;
                        // Retain local/inbox material and retry next sync. Never mark an
                        // unacknowledged protection request as durable recovery success.
                        addLog(
                          "Validated inbox notes are not yet confirmed in protected recovery storage",
                          "warning",
                        );
                      }
                    })();
                    if (!isCurrent()) return;
                    if (authoritative.spent.size > 0) {
                      for (const commitment of authoritative.spent) {
                        void markSpent(commitment);
                      }
                      addLog(
                        `Removed ${authoritative.spent.size} spent fresh-pool note(s)`,
                        "info",
                      );
                    }
                    if (authoritative.rejected.size > 0) {
                      addLog(
                        `Rejected ${authoritative.rejected.size} invalid inbox note(s)`,
                        "warning",
                      );
                    }
                    return true;
                  }

                  const validationUpdates = new Map<
                    string,
                    { leafIndex: number; treeIndex?: number; treeKey?: string }
                  >();
                  const validationSpent = new Set<string>();
                  const validationUnresolved = new Set<string>();

                  for (const stored of imported) {
                    const commitment = stored.note.commitment;
                    const commitmentKey = commitment.toLowerCase();
                    if (spentCommitments.has(commitmentKey)) continue;
                    let resolvedLeafIndex = stored.note.leafIndex;
                    const resolvedTreeIndex = stored.note.treeIndex;
                    const resolvedTreeKey = stored.note.treeKey;
                    if (resolvedLeafIndex < 0) {
                      resolvedLeafIndex =
                        (await findLeafIndexByMerkle(
                          commitment,
                          resolvedTreeIndex,
                          resolvedTreeKey,
                        )) ?? -1;
                    }

                    if (resolvedLeafIndex < 0) {
                      validationUnresolved.add(commitmentKey);
                      continue;
                    }

                    try {
                      const nullifier = await computeNullifier(
                        wallet.spendingKeys!.nullifyingKey,
                        stored.note.tokenHash,
                        resolvedLeafIndex,
                      );
                      const spentOnChain = await pool.nullifierUsed(nullifier);
                      if (spentOnChain) {
                        await markSpent(commitment);
                        validationSpent.add(commitmentKey);
                        continue;
                      }
                      validationUpdates.set(commitmentKey, {
                        leafIndex: resolvedLeafIndex,
                        treeIndex: resolvedTreeIndex,
                        treeKey: resolvedTreeKey,
                      });
                    } catch {
                      validationUnresolved.add(commitmentKey);
                    }
                  }

                  if (
                    validationUpdates.size > 0 ||
                    validationSpent.size > 0 ||
                    validationUnresolved.size > 0
                  ) {
                    setNotes((currentNotes) =>
                      !isCurrent()
                        ? currentNotes
                        : currentNotes
                            .filter(
                              (stored) =>
                                !validationSpent.has(
                                  stored.note.commitment.toLowerCase(),
                                ),
                            )
                            .map((stored) => {
                              const key = stored.note.commitment.toLowerCase();
                              const update = validationUpdates.get(key);
                              if (update) {
                                return {
                                  ...stored,
                                  note: {
                                    ...stored.note,
                                    leafIndex: update.leafIndex,
                                    treeIndex: update.treeIndex,
                                    treeKey: update.treeKey,
                                  },
                                };
                              }
                              if (validationUnresolved.has(key)) {
                                return {
                                  ...stored,
                                  note: {
                                    ...stored.note,
                                    leafIndex: -1,
                                  },
                                };
                              }
                              return stored;
                            }),
                    );
                  }

                  if (validationSpent.size > 0) {
                    addLog(
                      `Removed ${validationSpent.size} spent note(s) during balance validation`,
                      "info",
                    );
                  }
                } catch (error) {
                  console.warn("[Base] Balance validation skipped:", error);
                  throw error;
                } finally {
                  setPendingBalanceValidationCommitments((current) => {
                    if (!isCurrent()) return current;
                    const next = new Set(current);
                    for (const key of validationCommitments) next.delete(key);
                    return next;
                  });
                }
              })();
            } catch (err) {
              if (!isCurrent()) return;
              const msg = err instanceof Error ? err.message : String(err);
              addLog(`Inbox sync failed: ${msg}`, "warning");
              throw err;
            }
          },
          "Balance refresh is taking too long. Your saved balance is retained. Please try again.",
          undefined,
          Math.max(
            45_000,
            (notesRef.current.length + (prepared?.notes.length ?? 0)) * 500,
          ),
        );
      } finally {
        setPrivateBalanceSyncing((current) =>
          scopeCurrent() ? false : current,
        );
        operation.finish();
      }
    },
    [
      wallet.spendingKeys,
      wallet.connected,
      wallet.signer,
      wallet.address,
      authHeadersForPath,
      setNotes,
      mergeHistoryEntries,
      storeHistoryEvent,
      addLog,
      NOTES_API_URL,
      pendingSpentCommitments,
      prepareInboxSnapshot,
      walletSyncScope,
    ],
  );

  // Keep notesRef in sync so resolveUnindexedNotes can read notes without
  // taking them as a dep (which would cause an infinite re-trigger loop).
  useEffect(() => {
    notesRef.current = notes;
  }, [notes]);

  useEffect(() => {
    const cacheSecret = derivationSignatureB64Ref.current;
    if (!wallet.connected || !wallet.address || !cacheSecret) return;
    void saveEncryptedBaseNotes(wallet.address, notes, cacheSecret);
  }, [notes, wallet.address, wallet.connected]);

  /** Validates local notes against the chain; `only` limits a flat pass to
   * those notes (the others keep their current state). */
  const resolveUnindexedNotes = useCallback(
    async (only?: typeof notes) => {
      const isCurrent = walletSyncGuardRef.current.capture(walletSyncScope);
      if (!isCurrent()) return;
      const provider = EVM_CONFIG.JOINSPLIT_V2.ENABLED
        ? getBaseReadProvider()
        : wallet.signer?.provider;
      if (!provider || !wallet.spendingKeys) return;

      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
        const candidates = (only ?? notesRef.current).filter(
          (stored) =>
            !stored.spent && stored.note.protocol === "flat-joinsplit-v2",
        );
        if (candidates.length === 0) return;
        const authoritative = await synchronizeFlatJoinSplitV2Notes({
          provider,
          spendingKeys: wallet.spendingKeys,
          notes: candidates,
        });
        if (!isCurrent()) return;
        setPendingBalanceValidationCommitments((current) =>
          isCurrent() ? new Set(authoritative.pending) : current,
        );
        setNotes((current) =>
          !isCurrent()
            ? current
            : current
                .filter((stored) => {
                  const key = stored.note.commitment.toLowerCase();
                  return (
                    !authoritative.spent.has(key) &&
                    !authoritative.rejected.has(key)
                  );
                })
                .map((stored) => {
                  const key = stored.note.commitment.toLowerCase();
                  return (
                    authoritative.validated.get(key) ||
                    (authoritative.pending.has(key)
                      ? { ...stored, note: { ...stored.note, leafIndex: -1 } }
                      : stored)
                  );
                }),
        );
        if (authoritative.validated.size > 0) {
          addLog(
            `Validated ${authoritative.validated.size} fresh-pool note(s) against finalized events`,
            "success",
          );
        }
        return;
      }

      const unindexed = notesRef.current.filter(
        (n) => !n.spent && n.note.leafIndex < 0,
      );
      if (unindexed.length === 0) return;

      const contractAddress = getActiveBaseShieldedPoolAddress();
      const merkleIface = new ethers.Interface([
        "event Deposit(bytes32 indexed commitment, uint256 leafIndex, address indexed token, uint256 amount, uint256 timestamp)",
        "event LeafInserted(bytes32 indexed commitment, uint256 leafIndex)",
      ]);

      const resolved = new Map<string, number>();
      for (const stored of unindexed) {
        const commitment = stored.note.commitment;
        try {
          const depositTopic0 = merkleIface.getEvent("Deposit")!.topicHash;
          const leafInsertedTopic0 =
            merkleIface.getEvent("LeafInserted")!.topicHash;
          const commitmentTopic = ethers.zeroPadValue(commitment, 32);
          const depositLogs = await provider.getLogs({
            address: contractAddress,
            topics: [depositTopic0, commitmentTopic],
            fromBlock: 0,
            toBlock: "latest",
          });
          const leafLogs = await provider.getLogs({
            address: contractAddress,
            topics: [leafInsertedTopic0, commitmentTopic],
            fromBlock: 0,
            toBlock: "latest",
          });
          for (const log of [...depositLogs, ...leafLogs]) {
            const parsed = merkleIface.parseLog({
              topics: log.topics as string[],
              data: log.data,
            });
            if (!parsed) continue;
            const idx = Number(parsed.args.leafIndex);
            if (Number.isFinite(idx) && idx >= 0) {
              resolved.set(commitment.toLowerCase(), idx);
              break;
            }
          }
        } catch {
          // skip this commitment
        }
      }

      if (resolved.size === 0) return;

      setNotes((current) => {
        if (!isCurrent()) return current;
        let changed = false;
        const next = current.map((n) => {
          if (n.note.leafIndex >= 0) return n;
          const idx = resolved.get(n.note.commitment.toLowerCase());
          if (idx != null && idx >= 0) {
            changed = true;
            return { ...n, note: { ...n.note, leafIndex: idx } };
          }
          return n;
        });
        return changed ? next : current;
      });
      addLog(
        `Resolved ${resolved.size} note(s) from on-chain events`,
        "success",
      );
    },
    [wallet.signer, wallet.spendingKeys, setNotes, addLog, walletSyncScope],
  );

  // After an unchanged sync head, only notes the local index committed at that
  // head does not account for can need the chain (notesUnknownAtUnchangedHead);
  // without such an index every note is validated, as before.
  const resolveNotesAfterUnchangedHead = useCallback(
    async (reading?: unknown) => {
      const cursors = (reading as SyncHeadReading | undefined)?.cursors;
      if (!EVM_CONFIG.JOINSPLIT_V2.ENABLED || !wallet.spendingKeys || !cursors)
        return resolveUnindexedNotes();
      const recipientKey = await hashMpkHex(
        formatMpkHex(wallet.spendingKeys.mpk),
      );
      const unknown = notesUnknownAtUnchangedHead({
        index: await loadLocalSyncIndex(recipientKey),
        headFingerprint: syncHeadFingerprint(cursors),
        notes: notesRef.current.filter(
          (stored) =>
            !stored.spent && stored.note.protocol === "flat-joinsplit-v2",
        ),
      });
      if (unknown === null) return resolveUnindexedNotes();
      if (unknown.length > 0) return resolveUnindexedNotes(unknown);
    },
    [wallet.spendingKeys, loadLocalSyncIndex, resolveUnindexedNotes],
  );

  // Stable refs so the effect below can call the latest versions of each
  // callback without listing them as deps (which would cause re-firing every
  // time notes state changes and rebuilds those callbacks).
  const resolveUnindexedNotesRef = useRef(resolveUnindexedNotes);
  useEffect(() => {
    resolveUnindexedNotesRef.current = resolveUnindexedNotes;
  }, [resolveUnindexedNotes]);

  const flatNoteValidationState = useMemo(() => {
    const candidates = EVM_CONFIG.JOINSPLIT_V2.ENABLED
      ? notes.filter(
          (stored) =>
            !stored.spent && stored.note.protocol === "flat-joinsplit-v2",
        )
      : [];
    return {
      hasAny: candidates.length > 0,
      hasPending: candidates.some((stored) => stored.note.leafIndex < 0),
    };
  }, [notes]);

  useEffect(() => {
    if (
      !flatNoteValidationState.hasAny ||
      !wallet.connected ||
      !wallet.spendingKeys
    ) {
      return;
    }
    return startRecoveryPolling({
      run: () => resolveUnindexedNotesRef.current(),
      runImmediately: false,
      isPaused: () =>
        document.hidden ||
        balanceSyncBusyRef.current ||
        hasActiveFlatJoinSplitV2Operation(),
      intervalMs: flatNoteValidationState.hasPending ? 30_000 : 5 * 60_000,
      onError: (error) => {
        const message = error instanceof Error ? error.message : String(error);
        addLog(`Finality validation delayed: ${message}`, "warning");
      },
    });
  }, [
    addLog,
    // Depend on the primitive state, not the object recreated after every
    // setNotes: otherwise validation re-mounts and starts itself repeatedly.
    flatNoteValidationState.hasAny,
    flatNoteValidationState.hasPending,
    wallet.connected,
    wallet.signer,
    wallet.spendingKeys,
  ]);

  useEffect(() => {
    void fetchWalletBalance();
  }, [fetchWalletBalance]);

  const tabs = useMemo(
    () =>
      EVM_CONFIG.JOINSPLIT_V2.ENABLED
        ? [
            { id: "shield" as const, label: "Add" },
            { id: "swap" as const, label: "Swap" },
            { id: "send" as const, label: "Send" },
            { id: "receive" as const, label: "Receive" },
            ...(anyPrivateBridgeDeployment()
              ? [{ id: "crosschain" as const, label: "Bridge" }]
              : []),
          ]
        : [
            { id: "shield" as const, label: "Add" },
            { id: "swap" as const, label: "Swap" },
            { id: "send" as const, label: "Send" },
            { id: "receive" as const, label: "Receive" },
            { id: "crosschain" as const, label: "Bridge" },
            { id: "perps" as const, label: "Perps" },
          ],
    [],
  );

  const privateBalancesByToken = useMemo(() => {
    const balances = new Map<string, bigint>();
    const unspent = notes.filter(
      (n) =>
        hasDisplayBalance(n) &&
        !pendingSpentCommitments.has(n.note.commitment.toLowerCase()) &&
        n.note.value > 0n,
    );
    for (const { note } of unspent) {
      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
        const tokenAddress = note.tokenData?.tokenAddress;
        if (
          note.protocol === "flat-joinsplit-v2" &&
          typeof tokenAddress === "string" &&
          ethers.isAddress(tokenAddress)
        ) {
          const key = tokenAddress.toLowerCase();
          balances.set(key, (balances.get(key) ?? 0n) + note.value);
        }
        continue;
      }
      for (const [addr, hash] of tokenHashMap.entries()) {
        if (note.tokenHash === hash) {
          balances.set(addr, (balances.get(addr) ?? 0n) + note.value);
          break;
        }
      }
    }
    return balances;
  }, [notes, tokenHashMap, pendingSpentCommitments]);

  const spendableNotes = useMemo(
    () =>
      notes.filter(
        (n) =>
          !n.spent &&
          !pendingSpentCommitments.has(n.note.commitment.toLowerCase()) &&
          Number.isInteger(n.note.leafIndex) &&
          n.note.leafIndex >= 0,
      ),
    [notes, pendingSpentCommitments],
  );

  const ownedNftNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const nft of ownedNftOptions) {
      const key = `${nft.contract.toLowerCase()}:${nft.tokenType}:${nft.tokenId}`;
      const fallbackName = `${nft.tokenType.toUpperCase()} #${nft.tokenId}`;
      map.set(key, nft.name?.trim() || fallbackName);
    }
    return map;
  }, [ownedNftOptions]);

  const privateNftBalances = useMemo(() => {
    const balances = new Map<string, { token: TokenInfo; balance: bigint }>();
    for (const { note } of spendableNotes) {
      const tokenData = note.tokenData;
      if (!tokenData) continue;
      const tokenType =
        tokenData.tokenType === NoteTokenType.ERC1155
          ? "erc1155"
          : tokenData.tokenType === NoteTokenType.ERC721
            ? "erc721"
            : tokenData.tokenType === NoteTokenType.VNFT
              ? "vnft"
              : null;
      if (!tokenType) continue;
      const addr = tokenData.tokenAddress;
      if (!ethers.isAddress(addr)) continue;
      const tokenSubID = (tokenData.tokenSubID || "0").trim();
      const wrappedAsset = tokenData.wrappedAsset;
      const wrappedUnderlyingTokenType =
        wrappedAsset?.tokenType === NoteTokenType.ERC1155
          ? "erc1155"
          : "erc721";
      const wrappedUnderlyingTokenAddress = wrappedAsset?.tokenAddress;
      const wrappedUnderlyingTokenSubID = wrappedAsset?.tokenSubID;
      const key =
        wrappedAsset &&
        tokenType === "vnft" &&
        wrappedUnderlyingTokenType === "erc1155"
          ? `wrapped-erc1155:${(wrappedUnderlyingTokenAddress || "").toLowerCase()}:${(wrappedUnderlyingTokenSubID || "").trim()}`
          : `${addr.toLowerCase()}:${tokenType}:${tokenSubID}`;
      const nameHint = wrappedAsset?.name || ownedNftNameMap.get(key);
      const symbol =
        tokenType === "erc721"
          ? "ERC721"
          : tokenType === "erc1155"
            ? "ERC1155"
            : "vSTEALTH";
      const token: TokenInfo = {
        address: ethers.getAddress(addr),
        symbol,
        name:
          nameHint ||
          (wrappedAsset
            ? "vStealth"
            : `${symbol} ${ethers.getAddress(addr).slice(0, 6)}...${ethers.getAddress(addr).slice(-4)}`),
        decimals: 0,
        isNative: false,
        tokenType,
        tokenSubID:
          wrappedAsset &&
          tokenType === "vnft" &&
          wrappedUnderlyingTokenType === "erc1155"
            ? wrappedUnderlyingTokenSubID || tokenSubID
            : tokenSubID,
        isWrappedNft: Boolean(wrappedAsset),
        wrappedClaimHash: wrappedAsset?.claimHash,
        wrappedVaultIdx: wrappedAsset?.vaultIdx,
        wrappedNonce: wrappedAsset?.nonce,
        wrappedUnderlyingTokenAddress,
        wrappedUnderlyingTokenType,
        wrappedUnderlyingTokenSubID,
        wrappedName: wrappedAsset?.name || nameHint,
      };
      const prev = balances.get(key);
      balances.set(key, {
        token,
        balance: (prev?.balance ?? 0n) + note.value,
      });
    }
    return balances;
  }, [spendableNotes, ownedNftNameMap]);

  const privateBalanceDisplay = useMemo(
    () => ethers.formatEther(privateBalance),
    [privateBalance],
  );

  const spendableNotesCount = useMemo(
    () => spendableNotes.length,
    [spendableNotes],
  );

  useDepositMergeWarmup(
    baseActionsEnabled &&
      !isPaymentMode &&
      walletOperationOpen &&
      activeTab === "shield" &&
      activeDepositToken
      ? `${chainId}:${wallet.address}:${activeDepositToken.address}`
      : null,
    loading || approving || operationStarting,
    spendableNotes.some(
      (note) =>
        note.note.value > 0n &&
        note.note.tokenData?.tokenAddress?.toLowerCase() ===
          activeDepositToken?.address.toLowerCase(),
    ),
  );

  const depositMax = useDepositMax({
    account: wallet.address,
    chainId: Number(chainId),
    signer: wallet.signer,
    token: activeDepositToken,
    balance: walletTokenBalance,
    amount: depositAmount,
    setAmount: setDepositAmount,
    prepare:
      baseActionsEnabled &&
      !loading &&
      !approving &&
      !reviewOperation &&
      walletTokenBalance !== null &&
      (isPaymentMode
        ? !paymentAmountLocked
        : walletOperationOpen && activeTab === "shield"),
  });
  const canEstimateDepositMax =
    !!activeDepositToken &&
    walletTokenBalance !== null &&
    baseActionsEnabled &&
    !loading &&
    !approving;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!activeSendToken) {
        if (!cancelled) setSelectedSendTokenHash(undefined);
        return;
      }
      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
        // V2 spend selection is by the authenticated plaintext token address;
        // each tree intentionally has a different pool-derived token hash.
        if (!cancelled) setSelectedSendTokenHash(undefined);
        return;
      }
      const hash = await deriveTokenHash(
        activeSendToken.address,
        baseTokenHashType(activeSendToken),
        normalizeTokenSubID(activeSendToken.tokenSubID),
      );
      if (!cancelled) setSelectedSendTokenHash(hash);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSendToken]);

  const sendTokenHash = selectedSendTokenHash;
  const spendablePrivateBalance = useMemo(() => {
    if (
      activeSendToken &&
      (activeSendToken.tokenType === "erc721" ||
        activeSendToken.tokenType === "erc1155" ||
        activeSendToken.tokenType === "vnft")
    ) {
      const selectedKey = tokenOptionValue(activeSendToken);
      for (const { token, balance } of privateNftBalances.values()) {
        if (tokenOptionValue(token) === selectedKey) {
          return balance;
        }
      }
      return 0n;
    }
    if (EVM_CONFIG.JOINSPLIT_V2.ENABLED) {
      if (!activeSendToken) return 0n;
      if (usesPrivateFees())
        return privateSpendSource(spendableNotes, activeSendToken.address)
          .amount;
      const selectedAddress = activeSendToken.address.toLowerCase();
      return spendableNotes
        .filter(
          (stored) =>
            stored.note.protocol === "flat-joinsplit-v2" &&
            stored.note.tokenData?.tokenAddress?.toLowerCase() ===
              selectedAddress,
        )
        .reduce((sum, stored) => sum + stored.note.value, 0n);
    }
    if (sendTokenHash === undefined) return 0n;
    return spendableNotes
      .filter((n) => n.note.tokenHash === sendTokenHash)
      .reduce((sum, n) => sum + n.note.value, 0n);
  }, [activeSendToken, privateNftBalances, spendableNotes, sendTokenHash]);

  const sendPrivateBalanceDisplay = useMemo(
    () =>
      ethers.formatUnits(
        spendablePrivateBalance,
        activeSendToken?.decimals ?? 18,
      ),
    [spendablePrivateBalance, activeSendToken],
  );

  const swapInputTokenHash = tokenHashMap.get(
    swapInputToken.address.toLowerCase(),
  );
  const swapInputPrivateBalance = useMemo(
    () =>
      getSwapInputPrivateBalance(
        notes,
        swapInputToken,
        pendingSpentCommitments,
        swapInputTokenHash,
      ),
    [notes, swapInputTokenHash, swapInputToken, pendingSpentCommitments],
  );
  const crossChainInputTokenHash = tokenHashMap.get(
    crossChainInputToken.address.toLowerCase(),
  );
  const crossChainInputPrivateBalance = useMemo(() => {
    if (crossChainInputTokenHash === undefined) return "";
    const bal = notes
      .filter(
        (n) =>
          !n.spent &&
          !pendingSpentCommitments.has(n.note.commitment.toLowerCase()) &&
          Number.isInteger(n.note.leafIndex) &&
          n.note.leafIndex >= 0 &&
          n.note.tokenHash === crossChainInputTokenHash,
      )
      .reduce((sum, n) => sum + n.note.value, 0n);
    return ethers.formatUnits(bal, crossChainInputToken.decimals);
  }, [
    notes,
    crossChainInputTokenHash,
    crossChainInputToken,
    pendingSpentCommitments,
  ]);

  const privateBalanceDisplayRows = useMemo(() => {
    const fungibleRows = walletTokens
      .map((token) => {
        const bal =
          privateBalancesByToken.get(token.address.toLowerCase()) ?? 0n;
        if (bal === 0n) return null;
        return {
          id: token.address.toLowerCase(),
          mint: token.address.toLowerCase(),
          symbol: token.symbol,
          amount: ethers.formatUnits(bal, token.decimals),
          isNft: false,
        };
      })
      .filter(
        (
          row,
        ): row is {
          id: string;
          mint: string;
          symbol: string;
          amount: string;
          isNft: boolean;
        } => row !== null,
      );

    const nftRows = Array.from(privateNftBalances.values()).map(
      ({ token, balance }) => ({
        id: tokenOptionValue(token),
        mint: token.address.toLowerCase(),
        symbol: getNftDisplayLabel(token),
        amount: balance.toString(),
        isNft: true,
      }),
    );

    return [...fungibleRows, ...nftRows];
  }, [walletTokens, privateBalancesByToken, privateNftBalances]);

  const privateBalanceValuation = useMemo(
    () => balanceUsdValue(privateBalanceDisplayRows, tokenPrices),
    [privateBalanceDisplayRows, tokenPrices],
  );
  // Markets lists only priced tokens this network's catalog supports.
  const marketPriceIds = useMemo(() => {
    const supported = new Set(
      walletTokens.map((token) => token.address.toLowerCase()),
    );
    return Object.fromEntries(
      Object.entries(EVM_PRICE_IDS).filter(([address]) =>
        supported.has(address),
      ),
    );
  }, [walletTokens]);
  const balancesSyncing =
    privateBalanceSyncing || pendingBalanceValidationCommitments.size > 0;

  const sendTokenOptions = useMemo(() => {
    const byKey = new Map<string, TokenInfo>();
    for (const token of walletTokens) {
      byKey.set(tokenOptionValue(token), token);
    }
    for (const { token } of privateNftBalances.values()) {
      byKey.set(tokenOptionValue(token), token);
    }
    return Array.from(byKey.values());
  }, [walletTokens, privateNftBalances]);

  // The public withdrawal the send form describes, priced by a live relayer
  // quote on networks that use them (DogeOS Chikyu); Base keeps its fees.
  const relayQuoteInput = useMemo<RelayFeeQuoteInput | null>(() => {
    if (
      activeTab !== "send" ||
      !activeSendToken?.address ||
      activeSendToken.tokenType === "erc721" ||
      activeSendToken.tokenType === "erc1155" ||
      activeSendToken.tokenType === "vnft"
    )
      return null;
    const decimals = activeSendToken.decimals ?? 18;
    try {
      if (sendBatchMode) {
        const recipients = sendBatchRows.map((row) => row.recipient.trim());
        if (
          recipients.length < 2 ||
          recipients.some((recipient) => !ethers.isAddress(recipient))
        )
          return null;
        const total = sendBatchRows.reduce(
          (sum, row) => sum + ethers.parseUnits(row.amount || "0", decimals),
          0n,
        );
        return {
          kind: "withdraw_batch",
          token: activeSendToken.address,
          amount: total,
          recipients,
          amounts: sendBatchRows.map((row) => row.amount),
        };
      }
      const recipient = sendRecipient.trim();
      if (!ethers.isAddress(recipient)) return null;
      return {
        kind: "withdraw",
        token: activeSendToken.address,
        amount: ethers.parseUnits(sendAmount || "0", decimals),
        recipients: [recipient],
      };
    } catch {
      return null;
    }
  }, [
    activeTab,
    activeSendToken,
    sendBatchMode,
    sendBatchRows,
    sendRecipient,
    sendAmount,
  ]);
  const relayFeeQuote = useRelayFeeQuote({
    input: relayQuoteInput,
    notes: spendableNotes,
    account: wallet.address,
    paused: loading || approving || operationStarting,
  });
  const { handleSend, handleBatchWithdraw, sendProgress } =
    useBaseSendOperation({
      signer: wallet.signer,
      spendingKeys: wallet.spendingKeys,
      notes,
      relayFeeFor: relayFeeQuote.relayFee,
      addLog,
      setStatus,
    });
  const {
    handleBalancesRefresh,
    onchainRecoveryComplete,
    onchainRecoveryFailed,
    handleHistoryRefresh,
    prepareBridgeAction,
    canUseBatchWithdraw,
    sendFeesApply,
    protocolFeeBps,
    privateFeePolicy,
    partnerFeeBps,
    sendProtocolFee,
    sendPartnerFee,
    batchSendTotalAmount,
    batchSendProtocolFee,
    batchSendPartnerFee,
    handleBatchFileUpload,
    enterBatchMode,
    complianceToast,
    perps,
    handleShieldClick,
    handleSendClick,
    handleSwapClick,
    handleCrossChainClick,
    handleCrossChainCancel,
    copyPaymentLink,
    copyPrivateAddress,
    handleWalletButton,
    isComplianceBlocked,
    isComplianceChecking,
  } = useBaseAppActions({
    relayFee: relayFeeQuote.relayFee,
    feeToken:
      isPaymentMode || activeTab === "shield"
        ? activeDepositToken
        : activeSendToken,
    feeDeposit: isPaymentMode || activeTab === "shield",
    feeNotes: spendableNotes,
    feeQuotePaused: loading || approving || operationStarting,
    depositMaxQuote: depositMax.quote,
    NOTES_API_URL,
    activeDepositToken,
    activeSendToken,
    addLog,
    approveTokenSpend,
    authHeadersForPath,
    baseCacheSecret: derivationSignatureB64Ref.current,
    chainId,
    configuredPerpsAssetToken,
    crossChainAmount,
    crossChainDestinationChain,
    crossChainDestinationTokenAddress,
    crossChainExecutionDestinationAuthority,
    crossChainExecutionDestinationRecipient,
    crossChainDestinationResolutionError,
    crossChainDestinationResolutionLoading,
    crossChainInputToken,
    crossChainOrderStatus,
    crossChainPublicRecipientAddress,
    crossChainQuote,
    crossChainQuoteError,
    crossChainQuoteReady,
    crossChainQuoteRequestKey,
    crossChainRecipientMode,
    crossChainSourceAmountBase,
    clearProxyAuthToken,
    depositAmount,
    walletTokenBalance,
    spendablePrivateBalance,
    depositRecipient,
    disconnectWallet,
    enableRangeScreening,
    ensureProxyAuthToken,
    fetchTokenBalance,
    fetchTokenPrices,
    fetchWalletBalance,
    handleBatchWithdraw,
    handleBridge,
    handleBridgeCancel,
    handleSend,
    handleShield,
    setDepositProgress,
    handleSwap,
    isPaymentMode,
    paymentLinkAmountInput,
    paymentLinkToken,
    prevWalletConnectedRef,
    pushHistoryEntry,
    screeningError,
    screeningStatus,
    sendAmount,
    sendBatchMode,
    sendBatchRows,
    sendMemo:
      !sendBatchMode && isStealthHandle(sendRecipient.trim()) ? sendMemo : "",
    sendRecipient,
    setBalancesRefreshing,
    setCrossChainOrderStatus,
    setCrossChainQuote,
    setCrossChainQuoteError,
    setCrossChainQuoteLoading,
    setDepositAmount,
    setDepositRecipient,
    setHistoryError,
    setHistoryLoading,
    setLastPaymentTxHash,
    setLoading,
    setNotes,
    setPaymentLinkAmountInput,
    setPaymentLinkToken,
    setPendingSpentCommitments,
    setSendAmount,
    setSendBatchMode,
    setSendBatchRows,
    setSendMemo,
    setSendRecipient,
    setStatus,
    setSwapAmount,
    setSwapQuote,
    setSwapQuoteError,
    setWalletModalOpen,
    storeHistoryEvent,
    swapAmount,
    swapInputToken,
    swapOutputToken,
    swapQuote,
    swapSlippageBps,
    syncHistoryEntries,
    syncInboxNotes,
    prepareInboxSnapshot,
    checkSyncHead,
    resolveUnindexedNotes,
    resolveNotesAfterUnchangedHead,
    walletSyncScope,
    proxyAuthReady,
    wallet,
  });

  const depositValidation = amountValidation({
    amount: depositAmount,
    decimals: activeDepositToken?.decimals ?? 18,
    symbol: activeDepositToken?.symbol ?? "ETH",
    balance: walletTokenBalance,
    native: activeDepositToken?.isNative,
    maxDeposit: depositMax.quote?.amount,
    policy: privateFeePolicy,
    action: "deposit",
  });
  const relayedWithdrawalForm =
    EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
    !usesPrivateFees() &&
    EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS &&
    ethers.isAddress(sendRecipient.trim());
  const sendValidation = amountValidation({
    amount: sendAmount,
    decimals: activeSendToken?.decimals ?? 18,
    symbol: activeSendToken?.symbol ?? "ETH",
    balance: spendablePrivateBalance,
    privateBalance: true,
    policy: privateFeePolicy,
    action: ethers.isAddress(sendRecipient.trim()) ? "withdraw" : "p2p",
    deductedFee: relayedWithdrawalForm
      ? (relayFeeQuote.relayFee ?? undefined)
      : undefined,
    feePending: relayedWithdrawalForm ? relayFeeQuote.pendingMessage : null,
  });
  const sendRecipientCheck = useSendRecipient(
    sendRecipient,
    baseActionsEnabled && activeTab === "send" && !sendBatchMode,
  );

  // Fee estimation is cancellable form work, not a transaction. Keep navigation
  // and amount editing available while MAX is waiting for a read/proof.
  const walletBusy = loading || approving || operationStarting;
  useBlockNetworkSwitch(walletBusy || proxyAuthPending);
  const copyClaimedHandle = async () => {
    if (!claimedHandle || !baseActionsEnabled) return;
    try {
      await navigator.clipboard.writeText(claimedHandle);
      setStatus({ type: "success", message: "Handle copied" });
    } catch {
      setStatus({
        type: "error",
        message: "Could not copy the handle. Please try again.",
      });
    }
  };
  const walletChainLabel =
    Number(chainId) === EVM_CONFIG.CHAIN_ID && EVM_CONFIG.NETWORK
      ? EVM_CONFIG.NETWORK.label
      : "Chain " + chainId;
  const walletPrivateAddress = useMemo(
    () =>
      wallet.spendingKeys
        ? formatMpkHex(wallet.spendingKeys.mpk) +
          ":" +
          formatInboxPublicKeyHex(wallet.spendingKeys.spendingKey)
        : "",
    [wallet.spendingKeys],
  );
  useEffect(() => {
    setReviewOperation(null);
  }, [wallet.address, chainId]);
  useEffect(() => {
    if (loading) {
      operationStartingRef.current = false;
      setOperationStarting(false);
    }
  }, [loading]);
  const openTransactionReview = (operation: "shield" | "send") => {
    if (
      walletBusy ||
      !baseActionsEnabled ||
      isComplianceBlocked ||
      isComplianceChecking
    )
      return;
    const token = operation === "shield" ? activeDepositToken : activeSendToken;
    try {
      if (!token) throw new Error("Select a valid token.");
      if (
        operation === "shield" &&
        privateTokenPool() &&
        !depositTokens.some(
          (t) => t.address.toLowerCase() === token.address.toLowerCase(),
        )
      )
        throw new Error("This token is not supported for deposits.");
      const amount =
        operation === "shield"
          ? token.tokenType === "erc721"
            ? "1"
            : depositAmount
          : sendAmount;
      if (
        !(operation === "send" && sendBatchMode) &&
        ethers.parseUnits(amount || "0", token.decimals) <= 0n
      )
        throw new Error("Enter an amount greater than zero.");
      if (operation === "send" && !sendBatchMode) {
        const recipient = sendRecipient.trim();
        if (
          !ethers.isAddress(recipient) &&
          !parsePrivateAddress(recipient) &&
          !isStealthHandle(recipient) &&
          !/^0x[0-9a-fA-F]{64}$/.test(recipient)
        )
          throw new Error(
            "Enter a private address, .stealth handle or wallet address.",
          );
      }
      const validation =
        operation === "shield" ? depositValidation : sendValidation;
      if (!(operation === "send" && sendBatchMode) && validation.error)
        throw Error(validation.error);
      if (operation === "send" && !sendBatchMode && !sendRecipientCheck.valid)
        throw Error(sendRecipientCheck.message);
      setStatus(null);
      if (operation === "shield") depositMax.acceptAmount();
      setReviewOperation(operation);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Check the transaction details.",
      });
    }
  };
  const confirmReviewedOperation = () => {
    if (
      !reviewOperation ||
      walletBusy ||
      operationStartingRef.current ||
      !baseActionsEnabled ||
      isComplianceBlocked ||
      isComplianceChecking
    )
      return;
    const operation = reviewOperation;
    const validation =
      operation === "shield" ? depositValidation : sendValidation;
    if (
      !(operation === "send" && sendBatchMode) &&
      (validation.error || (operation === "send" && !sendRecipientCheck.valid))
    ) {
      setReviewOperation(null);
      setStatus({
        type: "error",
        message: validation.error || sendRecipientCheck.message,
      });
      return;
    }
    approvePrivateFeePolicy(privateFeePolicy);
    const startId = ++operationStartIdRef.current;
    operationStartingRef.current = true;
    setOperationStarting(true);
    setReviewOperation(null);
    const action = operation === "shield" ? handleShieldClick : handleSendClick;
    void action()
      .catch((error) =>
        setStatus({
          type: "error",
          message:
            error instanceof Error
              ? error.message
              : "Transaction could not start.",
        }),
      )
      .finally(() => {
        if (operationStartIdRef.current === startId) {
          operationStartingRef.current = false;
          setOperationStarting(false);
        }
      });
  };
  const reviewToken =
    reviewOperation === "shield"
      ? activeDepositToken || selectedDepositToken
      : activeSendToken || selectedSendToken;
  const reviewAmount =
    reviewOperation === "shield"
      ? reviewToken.tokenType === "erc721"
        ? "1"
        : depositAmount
      : sendBatchMode
        ? String(batchSendTotalAmount)
        : sendAmount;
  const reviewIsWithdrawal =
    reviewOperation === "send" &&
    !sendBatchMode &&
    ethers.isAddress(sendRecipient.trim());
  let reviewFee = "0";
  if (reviewOperation === "send" && sendBatchMode && sendFeesApply) {
    reviewFee = String(batchSendProtocolFee + batchSendPartnerFee);
  }
  let reviewNetAmount: string | undefined;
  if (reviewIsWithdrawal && sendFeesApply) {
    try {
      const amount = ethers.parseUnits(
        reviewAmount || "0",
        reviewToken.decimals,
      );
      const bps = EVM_CONFIG.JOINSPLIT_V2.ENABLED
        ? EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS
          ? protocolFeeBps
          : 0
        : protocolFeeBps + partnerFeeBps;
      const fee =
        EVM_CONFIG.JOINSPLIT_V2.ENABLED && !usesPrivateFees()
          ? EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS
            ? (relayFeeQuote.relayFee?.(amount) ?? 0n)
            : 0n
          : (amount * BigInt(bps)) / 10_000n;
      reviewFee = ethers.formatUnits(fee, reviewToken.decimals);
      if (EVM_CONFIG.JOINSPLIT_V2.ENABLED)
        reviewNetAmount = ethers.formatUnits(
          amount - fee,
          reviewToken.decimals,
        );
    } catch {
      /* Invalid inputs are rejected before opening review and by the transaction handler. */
    }
  }
  // A quoted network shows no relayer fee until a fresh quote covers the
  // withdrawal, and review cannot be confirmed; confirming binds the fee shown.
  const reviewFeePending =
    reviewOperation === "send" && relayQuoteInput && !relayFeeQuote.relayFee
      ? (relayFeeQuote.pendingMessage ??
        "The network fee is unavailable for this withdrawal.")
      : null;

  if (isPaymentMode) {
    return (
      <>
        <ComplianceToast toast={complianceToast} />
        <BasePaymentPage
          paymentLinkError={paymentLinkError}
          privateFeePolicy={privateFeePolicy}
          status={status}
          depositRecipient={depositRecipient}
          recipientMpkMissing={!depositRecipient && !loading}
          recipientMpkInvalid={
            !!depositRecipient &&
            !parsePrivateAddress(depositRecipient) &&
            !parseMpk(depositRecipient)
          }
          selectedToken={activeDepositToken ?? selectedDepositToken}
          setSelectedToken={(token) => {
            if (token.address.startsWith("nft:any:")) {
              setSelectedDepositToken(token);
              setUseCustomDepositNft(true);
              setCustomDepositNftType(
                token.tokenType === "erc1155" ? "erc1155" : "erc721",
              );
              setCustomDepositNftAddress("");
              setDepositTokenSubID("");
              return;
            }
            setUseCustomDepositNft(false);
            setSelectedDepositToken(token);
          }}
          tokenOptions={BASE_PAYMENT_TOKEN_OPTIONS}
          useCustomNft={useCustomDepositNft}
          customNftAddress={customDepositNftAddress}
          setCustomNftAddress={setCustomDepositNftAddress}
          customNftType={customDepositNftType}
          setCustomNftType={setCustomDepositNftType}
          nftTokenId={depositTokenSubID}
          setNftTokenId={setDepositTokenSubID}
          ownedNftOptions={ownedNftOptions}
          discoveringNfts={discoveringNfts}
          nftDiscoveryError={nftDiscoveryError}
          selectedOwnedNftIdx={selectedOwnedDepositNftIdx}
          setSelectedOwnedNftIdx={setSelectedOwnedDepositNftIdx}
          discoverWalletNfts={discoverWalletNfts}
          depositAmount={depositAmount}
          setDepositAmount={depositMax.changeAmount}
          depositMax={depositMax}
          paymentAmountLocked={paymentAmountLocked}
          paymentMintLocked={paymentMintLocked}
          walletTokenBalance={walletTokenBalance}
          loading={loading}
          approving={approving}
          depositProgress={depositProgress}
          connected={wallet.connected}
          actionsEnabled={baseActionsEnabled}
          authMessage={
            wallet.connected && !proxyAuthReady
              ? proxyAuthPending
                ? "Confirm the sign-in request in your wallet."
                : "Sign in to access your private wallet."
              : null
          }
          walletAddress={wallet.address}
          handleShieldClick={handleShieldClick}
          lastPaymentTxHash={lastPaymentTxHash}
          getExplorerTxUrl={getExplorerTxUrl}
          addLog={addLog}
          complianceStatus={screeningStatus}
          complianceMessage={screeningMessage}
          eip6963Providers={eip6963Providers}
          connectWallet={connectWallet}
          disconnectWallet={disconnectWallet}
        />
      </>
    );
  }

  return (
    <>
      <ComplianceToast toast={complianceToast} />
      {walletModalOpen && (
        <WalletModal
          providers={walletChoices}
          onSelect={(provider, info, options) => {
            setWalletModalOpen(false);
            void connectWallet(provider, info.name, options);
          }}
          onClose={() => setWalletModalOpen(false)}
        />
      )}
      <BaseWalletShell
        previousPoolMode={PREVIOUS_POOL_MODE}
        connected={wallet.connected}
        walletAddress={wallet.address}
        claimedHandle={claimedHandle}
        handlesAvailable={handlesSupported}
        chainLabel={walletChainLabel}
        busy={walletBusy}
        status={status}
        activeTab={activeTab}
        onOperationVisibilityChange={setWalletOperationOpen}
        tabs={tabs}
        onSelectTab={(tab) => {
          setReviewOperation(null);
          setActiveTab(tab);
        }}
        onWallet={handleWalletButton}
        onCopyHandle={() => void copyClaimedHandle()}
        canCopyHandle={baseActionsEnabled && !!wallet.spendingKeys}
        balanceUsd={privateBalanceValuation.total}
        priceCaption={
          privateBalanceValuation.total === null
            ? "Price unavailable"
            : (EVM_CONFIG.CHAIN_ID === 8453
                ? "Estimated value"
                : "Testnet reference value") +
              (privateBalanceValuation.unpriced > 0
                ? ` · Partial: ${privateBalanceValuation.unpriced} unpriced assets`
                : "")
        }
        balancesReady={
          privateBalanceDisplayRows.length > 0 ||
          (onchainRecoveryEnabled
            ? onchainRecoveryComplete
            : recoveryReady && !balancesSyncing)
        }
        balanceUnavailable={
          wallet.connected &&
          (!recoveryReady ||
            (onchainRecoveryEnabled && onchainRecoveryFailed)) &&
          privateBalanceDisplayRows.length === 0
        }
        balanceCaption={
          privateBalanceDisplayRows.length === 1
            ? formatDisplayAmount(privateBalanceDisplayRows[0].amount) +
              " " +
              privateBalanceDisplayRows[0].symbol
            : privateBalanceDisplayRows.length
              ? privateBalanceDisplayRows.length + " private assets"
              : "No private assets yet"
        }
        refreshing={balancesRefreshing || balancesSyncing}
        canRefresh={recoveryReady}
        onRefresh={() => void handleBalancesRefresh()}
        reviewing={!!reviewOperation}
        onBackFromReview={() => setReviewOperation(null)}
        authNotice={
          <>
            {wallet.connected && !proxyAuthReady && (
              <div
                style={{
                  margin: "0 auto 16px",
                  maxWidth: "1200px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "12px 14px",
                  border: "1px solid rgba(248, 192, 140, 0.35)",
                  borderRadius: "10px",
                  background: "rgba(248, 192, 140, 0.08)",
                  color: "#f8d78c",
                  fontSize: "13px",
                }}
              >
                <div>
                  {proxyAuthPending
                    ? "Confirm the sign-in request in your wallet."
                    : proxyAuthError ||
                      (onchainRecoveryEnabled
                        ? "Sign in to submit sponsored transactions. Wallet recovery is available."
                        : "Sign in to access your private wallet.")}
                </div>
                <button
                  type="button"
                  style={{
                    ...styles.button,
                    ...styles.secondaryButton,
                    width: "auto",
                    marginBottom: 0,
                    padding: "8px 12px",
                    opacity: proxyAuthPending ? 0.6 : 1,
                  }}
                  disabled={proxyAuthPending}
                  onClick={() => void handleProxyAuthClick()}
                >
                  {proxyAuthPending ? "Waiting..." : "Sign In"}
                </button>
              </div>
            )}
          </>
        }
        assets={
          <BasePrivateBalancesCard
            connected={wallet.connected}
            balancesSyncing={balancesSyncing}
            rows={privateBalanceDisplayRows}
            tokenPrices={tokenPrices}
            tokenMarkets={marketPriceIds}
          />
        }
        portfolio={{
          rows: privateBalanceDisplayRows,
          prices: tokenPrices,
          changes: tokenPriceChanges,
          markets: marketPriceIds,
          syncing: balancesSyncing,
        }}
        history={
          <BaseHistoryCard
            historyLoading={historyLoading}
            historyError={historyError || historyPageError}
            historyEntries={historyEntries}
            hasMore={historyHasMore}
            loadingMore={historyPageLoading}
            onLoadMore={loadMoreHistory}
            onRefresh={handleHistoryRefresh}
            getExplorerTxUrl={getExplorerTxUrl}
          />
        }
        handle={
          <BaseHandlePanel
            canReceivePrivate={
              !!wallet.spendingKeys && baseActionsEnabled && !walletBusy
            }
            claimedHandle={claimedHandle}
            busy={handleBusy}
            registering={handleRegistering}
            handleInput={handleInput}
            setHandleInput={setHandleInput}
            registerHandle={registerHandle}
            checkHandleAvailability={checkHandleAvailability}
            copyHandle={copyHandle}
            handleStatus={handleStatus}
            networkLabel={handleNetworkLabelHere}
            claimedDetails={claimedHandleDetails}
            suggestedHandle={suggestedHandle}
            findOwnedHandle={findOwnedHandle}
            updateClaimedHandle={updateClaimedHandle}
            canShareAcrossNetworks={canShareHandleAcrossNetworks}
          />
        }
        recovery={
          EVM_CONFIG.JOINSPLIT_V2.ENABLED ? (
            <div className="wallet-recovery">
              <RecoveryPhraseBackup
                masterRecovery={wallet.spendingKeys?.masterRecovery}
                spendingKey={wallet.spendingKeys?.spendingKey ?? null}
                recoveryEntropy={wallet.spendingKeys?.recoveryEntropy}
                scopeLabel={EVM_CONFIG.NETWORK?.label ?? "Base"}
                walletIdentity={`${EVM_CONFIG.CHAIN_ID}:${wallet.address}:${wallet.spendingKeys?.mpk ?? ""}`}
              />
              {onchainRecoveryEnabled ? (
                <OnchainRecoveryPanel
                  ready={recoveryReady}
                  busy={balancesRefreshing || balancesSyncing}
                  logs={logs}
                  onRefresh={handleBalancesRefresh}
                />
              ) : (
                <BaseRecoveryPanel
                  context={recoveryContext}
                  session={walletSyncScope}
                  notes={notes}
                  logs={logs}
                  onRestoreNotes={restoreManagedRecoveryNotes}
                  onReconcile={handleBalancesRefresh}
                  onRetrySettlement={retryManagedRecoverySettlement}
                />
              )}
            </div>
          ) : null
        }
      >
        {reviewOperation && (
          <BaseTransactionReview
            kind={
              reviewOperation === "shield"
                ? "deposit"
                : sendBatchMode
                  ? "batch"
                  : reviewIsWithdrawal
                    ? "withdrawal"
                    : "private"
            }
            amount={reviewAmount}
            symbol={reviewToken.symbol}
            recipient={
              reviewOperation === "shield"
                ? "Your private wallet"
                : sendBatchMode
                  ? sendBatchRows
                      .map((row) => row.recipient + " · " + row.amount)
                      .join("; ")
                  : sendRecipient.trim()
            }
            network={walletChainLabel}
            fee={reviewFee}
            feePending={reviewFeePending}
            privateFeePolicy={usesPrivateFees() ? privateFeePolicy : undefined}
            depositEstimate={
              reviewOperation === "shield" ? depositMax.quote : null
            }
            feeLabel={
              reviewOperation === "shield"
                ? "Protocol fee"
                : EVM_CONFIG.JOINSPLIT_V2.ENABLED
                  ? "Relayer fee"
                  : "Total fee"
            }
            receiveAmount={reviewNetAmount}
            delivery={
              reviewIsWithdrawal && EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS
                ? "The relayer submits the transaction."
                : undefined
            }
            disabled={
              walletBusy ||
              !baseActionsEnabled ||
              isComplianceBlocked ||
              isComplianceChecking
            }
            onConfirm={confirmReviewedOperation}
          />
        )}
        <div hidden={!!reviewOperation}>
          <fieldset
            className="wallet-form"
            disabled={
              walletBusy && (activeTab === "shield" || activeTab === "send")
            }
          >
            {activeTab === "shield" && (
              <div>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#b0c4de",
                    marginBottom: "16px",
                  }}
                >
                  Deposit tokens into your private wallet. You will be prompted
                  to sign the transaction.
                </p>
                <div style={{ marginBottom: "12px" }}>
                  <label
                    htmlFor="wallet-deposit-token"
                    style={{
                      ...styles.label,
                      color: "#b0c4de",
                      fontSize: "13px",
                    }}
                  >
                    Token
                  </label>
                  <select
                    id="wallet-deposit-token"
                    value={
                      useCustomDepositNft
                        ? "__custom_nft__"
                        : selectedDepositToken.address
                    }
                    onChange={(e) => {
                      if (e.target.value === "__custom_nft__") {
                        setUseCustomDepositNft(true);
                        return;
                      }
                      setUseCustomDepositNft(false);
                      const t = walletTokens.find(
                        (tk) => tk.address === e.target.value,
                      );
                      if (t) setSelectedDepositToken(t);
                    }}
                    style={
                      {
                        ...styles.input,
                        color: "#e8eeff",
                        cursor: "pointer",
                        appearance: "none",
                        WebkitAppearance: "none",
                        backgroundImage:
                          'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27%3E%3Cpath d=%27M1 1l5 5 5-5%27 stroke=%27%23b0c4de%27 fill=%27none%27/%3E%3C/svg%3E")',
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 12px center",
                        paddingRight: "32px",
                      } as React.CSSProperties
                    }
                  >
                    {!EVM_CONFIG.JOINSPLIT_V2.ENABLED && (
                      <option
                        value="__custom_nft__"
                        style={{ background: "#1a1a2e", color: "#e8eeff" }}
                      >
                        Custom NFT (runtime)
                      </option>
                    )}
                    {BASE_DEPOSIT_TOKEN_OPTIONS.map((t) => (
                      <option
                        key={t.address}
                        value={t.address}
                        style={{ background: "#1a1a2e", color: "#e8eeff" }}
                      >
                        {t.symbol} – {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ marginBottom: "16px" }}>
                  {(useCustomDepositNft ||
                    selectedDepositToken.tokenType === "erc721" ||
                    selectedDepositToken.tokenType === "erc1155") && (
                    <div
                      style={{
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: "10px",
                        padding: "10px",
                        marginBottom: "10px",
                        background: "rgba(255,255,255,0.03)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#9cb0d0",
                          marginBottom: "8px",
                        }}
                      >
                        NFT Shield Card
                      </div>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          fontSize: "12px",
                          color: "#c9d8f2",
                          marginBottom: "10px",
                        }}
                      >
                        <input type="checkbox" checked={true} disabled />
                        Private Wrapped NFT mode (claim-ID obfuscation)
                      </label>
                      <NftDiscoveryFields
                        connected={wallet.connected}
                        discoveringNfts={discoveringNfts}
                        ownedNftOptions={ownedNftOptions}
                        nftDiscoveryError={nftDiscoveryError}
                        selectedOwnedNftIdx={selectedOwnedDepositNftIdx}
                        onDiscover={() => void discoverWalletNfts()}
                        onSelectOwnedNftIdx={(value) => {
                          setSelectedOwnedDepositNftIdx(value);
                          const idx = Number(value);
                          const picked = Number.isFinite(idx)
                            ? ownedNftOptions[idx]
                            : undefined;
                          if (!picked) return;
                          setUseCustomDepositNft(true);
                          setCustomDepositNftAddress(picked.contract);
                          setCustomDepositNftType(picked.tokenType);
                          setDepositTokenSubID(picked.tokenId);
                        }}
                      />
                      <label
                        style={{
                          ...styles.label,
                          color: "#b0c4de",
                          fontSize: "12px",
                        }}
                      >
                        NFT Contract
                      </label>
                      <input
                        style={styles.input}
                        type="text"
                        value={
                          useCustomDepositNft
                            ? customDepositNftAddress
                            : (activeDepositToken?.address ??
                              selectedDepositToken.address)
                        }
                        onChange={(e) =>
                          setCustomDepositNftAddress(e.target.value)
                        }
                        disabled={!useCustomDepositNft}
                        placeholder="0x..."
                      />
                      <label
                        style={{
                          ...styles.label,
                          color: "#b0c4de",
                          fontSize: "12px",
                        }}
                      >
                        NFT Standard
                      </label>
                      <select
                        value={
                          useCustomDepositNft
                            ? customDepositNftType
                            : activeDepositToken?.tokenType === "erc1155"
                              ? "erc1155"
                              : "erc721"
                        }
                        onChange={(e) =>
                          setCustomDepositNftType(
                            e.target.value as "erc721" | "erc1155",
                          )
                        }
                        disabled={!useCustomDepositNft}
                        style={
                          {
                            ...styles.input,
                            color: "#e8eeff",
                            cursor: useCustomDepositNft
                              ? "pointer"
                              : "not-allowed",
                          } as React.CSSProperties
                        }
                      >
                        <option
                          value="erc721"
                          style={{ background: "#1a1a2e", color: "#e8eeff" }}
                        >
                          ERC-721
                        </option>
                        <option
                          value="erc1155"
                          style={{ background: "#1a1a2e", color: "#e8eeff" }}
                        >
                          ERC-1155
                        </option>
                      </select>
                      <label
                        style={{
                          ...styles.label,
                          color: "#b0c4de",
                          fontSize: "12px",
                        }}
                      >
                        Token ID
                      </label>
                      <input
                        style={{ ...styles.input, marginBottom: 0 }}
                        type="text"
                        value={depositTokenSubID}
                        onChange={(e) => setDepositTokenSubID(e.target.value)}
                        placeholder="e.g. 101"
                      />
                    </div>
                  )}
                  <label
                    htmlFor="wallet-deposit-amount"
                    style={{
                      ...styles.label,
                      color: "#b0c4de",
                      fontSize: "13px",
                    }}
                  >
                    Amount (
                    {activeDepositToken?.symbol ?? selectedDepositToken.symbol})
                  </label>
                  <WalletAmountInput
                    id="wallet-deposit-amount"
                    value={
                      activeDepositToken?.tokenType === "erc721"
                        ? "1"
                        : depositAmount
                    }
                    onChange={(e) => depositMax.changeAmount(e.target.value)}
                    disabled={
                      paymentAmountLocked ||
                      activeDepositToken?.tokenType === "erc721"
                    }
                    placeholder={
                      activeDepositToken?.tokenType === "erc721" ? "1" : "0.01"
                    }
                    onMax={() => void depositMax.selectMax()}
                    maxDisabled={
                      !canEstimateDepositMax ||
                      paymentAmountLocked ||
                      activeDepositToken?.tokenType === "erc721"
                    }
                  />
                  {wallet.connected && depositValidation.error && (
                    <p
                      role="status"
                      style={{
                        color: "#ff5263",
                        fontSize: "13px",
                        margin: "6px 0 12px",
                      }}
                    >
                      {depositValidation.error}
                    </p>
                  )}
                  {walletTokenBalance !== null && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#b0c4de",
                        marginTop: "4px",
                      }}
                    >
                      Available:{" "}
                      {parseFloat(walletTokenBalance).toFixed(
                        (activeDepositToken?.decimals ??
                          selectedDepositToken.decimals) <= 6
                          ? 2
                          : 4,
                      )}{" "}
                      {activeDepositToken?.symbol ??
                        selectedDepositToken.symbol}
                    </div>
                  )}
                  {usesPrivateFees() ? (
                    <PrivateFeeSummary
                      policy={privateFeePolicy}
                      action="deposit"
                      amount={depositAmount}
                    />
                  ) : (
                    !depositMax.quote && (
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#b0c4de",
                          marginTop: "6px",
                        }}
                      >
                        Protocol fee (0 bps): 0.000000{" "}
                        {activeDepositToken?.symbol ??
                          selectedDepositToken.symbol}
                      </div>
                    )
                  )}
                  <BaseDepositEstimate {...depositMax} />
                </div>
                {walletBusy && (
                  <BaseOperationProgress
                    message={depositProgress || "Preparing transaction…"}
                  />
                )}
                <button
                  className="wallet-cta"
                  style={{ ...styles.button, ...styles.primaryButton }}
                  onClick={() =>
                    wallet.connected
                      ? openTransactionReview("shield")
                      : handleWalletButton()
                  }
                  disabled={
                    wallet.connected &&
                    (!!depositValidation.error ||
                      depositMax.pending ||
                      !!depositMax.error ||
                      (usesPrivateFees() && !privateFeePolicy) ||
                      !proxyAuthReady ||
                      loading ||
                      approving ||
                      isComplianceBlocked ||
                      isComplianceChecking)
                  }
                >
                  {!wallet.connected
                    ? "Connect Wallet"
                    : !proxyAuthReady
                      ? proxyAuthPending
                        ? "Waiting for Sign In"
                        : "Sign In Required"
                      : approving
                        ? "Approving..."
                        : loading
                          ? "Processing..."
                          : "Continue"}
                </button>
              </div>
            )}

            {activeTab === "receive" && (
              <BaseReceiveTab
                canReceivePrivate={!!wallet.spendingKeys && baseActionsEnabled}
                paymentLinkToken={paymentLinkToken}
                setPaymentLinkToken={setPaymentLinkToken}
                paymentLinkAmountInput={paymentLinkAmountInput}
                setPaymentLinkAmountInput={setPaymentLinkAmountInput}
                copyPaymentLink={() => void copyPaymentLink()}
                copyPrivateAddress={copyPrivateAddress}
                claimedHandle={claimedHandle}
                copyHandle={() => void copyClaimedHandle()}
                privateAddress={walletPrivateAddress}
                tokenOptions={BASE_PAYMENT_TOKEN_OPTIONS}
              />
            )}

            {activeTab === "send" && (
              <>
                {privateTokenPool() &&
                  !selectedSendToken.isNative &&
                  !depositTokens.some(
                    (t) =>
                      t.address.toLowerCase() ===
                      selectedSendToken.address.toLowerCase(),
                  ) && (
                    <p role="status">
                      This asset is no longer available for private sends. You
                      can withdraw it to a public wallet.
                    </p>
                  )}
                <BaseSendTab
                  relayFee={relayFeeQuote.relayFee}
                  relayFeePending={relayFeeQuote.pendingMessage}
                  recipientCheck={sendRecipientCheck}
                  canUseBatchWithdraw={canUseBatchWithdraw}
                  sendTokenOptions={sendTokenOptions}
                  selectedSendToken={selectedSendToken}
                  setSelectedSendToken={setSelectedSendToken}
                  sendBatchMode={sendBatchMode}
                  setSendBatchMode={setSendBatchMode}
                  batchFileInputRef={
                    batchFileInputRef as React.RefObject<HTMLInputElement>
                  }
                  handleBatchFileUpload={handleBatchFileUpload}
                  sendBatchRows={sendBatchRows}
                  setSendBatchRows={setSendBatchRows}
                  setSendRecipient={setSendRecipient}
                  setSendAmount={setSendAmount}
                  sendRecipient={sendRecipient}
                  sendAmount={sendAmount}
                  enterBatchMode={enterBatchMode}
                  activeSendToken={activeSendToken}
                  sendPrivateBalanceDisplay={sendPrivateBalanceDisplay}
                  spendablePrivateBalance={spendablePrivateBalance}
                  sendMemo={sendMemo}
                  setSendMemo={setSendMemo}
                  sendFeesApply={sendFeesApply}
                  sendProgress={
                    operationStarting ? "Checking access…" : sendProgress
                  }
                  preparationVisible={walletOperationOpen}
                  protocolFeeBps={protocolFeeBps}
                  privateFeePolicy={privateFeePolicy}
                  partnerFeeBps={partnerFeeBps}
                  batchSendProtocolFee={batchSendProtocolFee}
                  sendProtocolFee={sendProtocolFee}
                  batchSendPartnerFee={batchSendPartnerFee}
                  sendPartnerFee={sendPartnerFee}
                  loading={walletBusy}
                  connected={baseActionsEnabled}
                  isComplianceBlocked={isComplianceBlocked}
                  isComplianceChecking={isComplianceChecking}
                  onSend={() => openTransactionReview("send")}
                />
              </>
            )}

            {activeTab === "swap" && (
              <BaseSwapTab
                enabled={EVM_CONFIG.PRIVATE_SWAP_ENABLED}
                chainLabel={walletChainLabel}
                tokens={walletTokens}
                inputToken={swapInputToken}
                setInputToken={setSwapInputToken}
                outputToken={swapOutputToken}
                setOutputToken={setSwapOutputToken}
                amount={swapAmount}
                setAmount={setSwapAmount}
                slippageBps={swapSlippageBps}
                setSlippageBps={setSwapSlippageBps}
                inputPrivateBalance={swapInputPrivateBalance}
                quote={swapQuote}
                quoteLoading={swapQuoteLoading}
                quoteError={swapQuoteError}
                onRefreshQuote={() =>
                  setSwapQuoteRefreshKey((value) => value + 1)
                }
                loading={walletBusy}
                connected={
                  baseActionsEnabled &&
                  !isComplianceBlocked &&
                  !isComplianceChecking
                }
                spendingKeysReady={!!wallet.spendingKeys}
                onSwap={handleSwapClick}
                progressMessage={
                  status?.type === "info" ? status.message : undefined
                }
                completed={status?.type === "success"}
              />
            )}

            {activeTab === "crosschain" && anyPrivateBridgeDeployment() && (
              <PrivateBridgePanel
                active={walletOperationOpen}
                connected={
                  baseActionsEnabled &&
                  !isComplianceBlocked &&
                  !isComplianceChecking
                }
                busy={walletBusy}
                account={wallet.address}
                signer={wallet.signer}
                spendingKeys={wallet.spendingKeys}
                tokens={walletTokens}
                notes={spendableNotes}
                onBusy={setLoading}
                onStatus={setStatus}
                beforeSubmit={prepareBridgeAction}
                onSettled={(result, meta) => {
                  if (meta && result.txHash) {
                    // Bridges belong in the activity list like deposits and withdrawals.
                    const entry = {
                      type: "bridge" as const,
                      eventId: `bridge:${result.txHash.toLowerCase()}`,
                      amount: meta.amount,
                      symbol: meta.symbol,
                      memo: `To ${meta.destinationLabel} via ${meta.providerName}`,
                      txHash: result.txHash,
                    };
                    mergeHistoryEntries([{ ...entry, timestamp: Date.now() }]);
                    void storeHistoryEvent(entry).catch(() => undefined);
                  }
                  const spent = new Set(
                    result.spentNotes.map((n) => n.commitment.toLowerCase()),
                  );
                  setPendingSpentCommitments(
                    (previous) => new Set([...previous, ...spent]),
                  );
                  setNotes((previous) => {
                    const next = previous.map((n) =>
                      spent.has(n.note.commitment.toLowerCase())
                        ? { ...n, spent: true }
                        : n,
                    );
                    for (const note of result.outputNotes)
                      if (
                        !next.some((n) => n.note.commitment === note.commitment)
                      )
                        next.push({
                          note,
                          timestamp: Date.now(),
                          txHash: result.txHash,
                        });
                    return next;
                  });
                  void handleBalancesRefresh();
                }}
              />
            )}
            {activeTab === "crosschain" && !anyPrivateBridgeDeployment() && (
              <BaseCrossChainTab
                tokens={bridgeableTokens}
                inputToken={crossChainInputToken}
                setInputToken={setCrossChainInputToken}
                amount={crossChainAmount}
                setAmount={setCrossChainAmount}
                inputPrivateBalance={crossChainInputPrivateBalance}
                destinationChain={crossChainDestinationChain}
                setDestinationChain={setCrossChainDestinationChain}
                destinationAddress={crossChainDestinationAddress}
                setDestinationAddress={setCrossChainDestinationAddress}
                destinationResolved={
                  crossChainRecipientMode === "public" &&
                  crossChainPublicRecipientAddress
                    ? {
                        mode: "public_eoa" as const,
                        handle: crossChainPublicRecipientAddress,
                        chainScope: crossChainDestinationChain,
                        recipientKeyMasked: maskHex(
                          crossChainPublicRecipientAddress,
                        ),
                      }
                    : null
                }
                destinationResolutionLoading={
                  crossChainDestinationResolutionLoading
                }
                destinationResolutionError={
                  crossChainDestinationResolutionError
                }
                loading={walletBusy}
                connected={
                  baseActionsEnabled &&
                  !isComplianceBlocked &&
                  !isComplianceChecking
                }
                spendingKeysReady={!!wallet.spendingKeys}
                quoteLoading={crossChainQuoteLoading}
                quote={crossChainQuote}
                quoteReady={crossChainQuoteReady}
                quoteError={crossChainQuoteError}
                orderStatus={crossChainOrderStatus}
                onBridge={handleCrossChainClick}
                onCancelOrder={handleCrossChainCancel}
              />
            )}

            {activeTab === "perps" && (
              <BasePerpsTab
                connected={
                  baseActionsEnabled &&
                  !isComplianceBlocked &&
                  !isComplianceChecking
                }
                loading={walletBusy}
                privateBalanceUsd={
                  privateBalanceValuation.unpriced
                    ? null
                    : privateBalanceValuation.total
                }
                perps={perps}
                showAdvanced={
                  (
                    process.env.REACT_APP_BASE_PERPS_ADVANCED_UI || ""
                  ).toLowerCase() === "true"
                }
              />
            )}
          </fieldset>
        </div>
      </BaseWalletShell>
    </>
  );
}
